import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminJobDetailView from "./AdminJobDetailView";
import { Y, T, inp, CREDIT } from "../theme";
import { CreditIcon, ModelIcon, ResPill, GeminiLogoShared } from "./shared";
import { api, serverNow } from "../firebase";
import { useNotify, useHeaderRight, useConfirm, useRefreshProfile } from "../App";
import { useTheme } from "../useTheme";
import PageLoader from "./PageLoader";

const ROLES = ["owner", "admin", "creator", "viewer"];
const ROLE_COLORS = { owner: Y, admin: "#44BBFF", creator: "#5BBFB8", viewer: T.text3 };
const SIDEBAR_BORDER = "rgba(255,255,255,0.04)";

function RoleBadge({ role, editable, onChangeRole }) {
  const color = ROLE_COLORS[role] || T.text3;
  if (editable) {
    return (
      <div style={{position:"relative",display:"inline-flex"}}>
        <select
          value={role}
          onChange={e => onChangeRole(e.target.value)}
          style={{
            appearance:"none", WebkitAppearance:"none", MozAppearance:"none",
            padding:"2px 22px 2px 10px", borderRadius:12,
            border:`1px solid ${color}50`, background:`${color}12`,
            color, fontSize:11, fontWeight:700, textTransform:"uppercase",
            cursor:"pointer", outline:"none", letterSpacing:"0.04em",
            fontFamily:"inherit",
          }}
        >
          {ROLES.filter(r => r !== "owner").map(r => (
            <option key={r} value={r} style={{background:T.dark,color:ROLE_COLORS[r]||T.text3,textTransform:"uppercase"}}>{r}</option>
          ))}
        </select>
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" style={{position:"absolute",right:7,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
    );
  }
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      padding:"2px 10px", borderRadius:12,
      border:`1px solid ${color}50`, background:`${color}12`,
      color, fontSize:11, fontWeight:700, textTransform:"uppercase",
      letterSpacing:"0.04em",
    }}>
      {role}
    </span>
  );
}


// Slim id+label registry for the Model Control admin UI. Kept in sync
// manually with GenerateView's MODELS / IMAGE_MODELS arrays - the admin
// panel only needs IDs + display labels, not the pricing/capability
// fields. ORDER MATTERS: these are listed in the exact order the model
// pickers render them, so the admin tiles match what users see:
//   Video picker sort: resolution desc (4K>1080p>720p>480p) ->
//     provider (Grok > Kling > Veo) -> version desc.
//   Image picker sort: ref-image tier desc (2+ > 1 > none) ->
//     max resolution desc -> provider (Gemini>FLUX>Kling>Grok>Recraft)
//     -> qualityRank desc.
// If you add/reorder a model in GenerateView, mirror it here.
const VIDEO_MODEL_REGISTRY = [
  // ── 4K ──
  { id: 'kling-3.0-4k',        label: 'Kling 3.0 4K' },
  { id: 'kling-3.0-omni-4k',   label: 'Kling 3.0 Omni 4K' },
  { id: 'google-omni-flash-4k',      label: 'Gemini Omni Flash 4K' },
  { id: 'google-veo-3.1-4k',         label: 'Veo 3.1 4K' },
  { id: 'google-veo-3.1-fast-4k',    label: 'Veo 3.1 Fast 4K' },
  // ── 1080p ──
  { id: 'kling-3.0-pro',       label: 'Kling 3.0 Pro' },
  { id: 'kling-3.0-omni-pro',  label: 'Kling 3.0 Omni Pro' },
  { id: 'kling-3.0-turbo-1080p', label: 'Kling 3.0 Turbo' },
  { id: 'kling-o1-pro',        label: 'Kling O1 Pro' },
  { id: 'kling-2.6-pro',       label: 'Kling 2.6 Pro' },
  { id: 'kling-2.5-turbo-pro', label: 'Kling 2.5T Pro' },
  { id: 'kling-2.1-master',    label: 'Kling 2.1 Master' },
  { id: 'kling-2.1-pro',       label: 'Kling 2.1 Pro' },
  { id: 'kling-1.6-pro',       label: 'Kling 1.6 Pro' },
  { id: 'kling-1.5-pro',       label: 'Kling 1.5 Pro' },
  { id: 'google-omni-flash-1080p',   label: 'Gemini Omni Flash' },
  { id: 'google-veo-3.1-1080p',      label: 'Veo 3.1' },
  { id: 'google-veo-3.1-fast-1080p', label: 'Veo 3.1 Fast' },
  { id: 'google-veo-3.1-lite-1080p', label: 'Veo 3.1 Lite' },
  // ── 720p ──
  { id: 'grok-imagine-720p',   label: 'Grok Imagine HD' },
  { id: 'kling-3.0',           label: 'Kling 3.0' },
  { id: 'kling-3.0-omni',      label: 'Kling 3.0 Omni' },
  { id: 'kling-3.0-turbo-720p', label: 'Kling 3.0 Turbo 720p' },
  { id: 'kling-o1',            label: 'Kling O1' },
  { id: 'kling-2.6',           label: 'Kling 2.6' },
  { id: 'kling-2.5-turbo',     label: 'Kling 2.5 Turbo' },
  { id: 'kling-2.1',           label: 'Kling 2.1' },
  { id: 'kling-2.0-master',    label: 'Kling 2.0 Master' },
  { id: 'kling-1.6',           label: 'Kling 1.6' },
  { id: 'kling-1.5',           label: 'Kling 1.5' },
  { id: 'kling-effects',       label: 'Kling Effects' },
  { id: 'google-omni-flash-720p',    label: 'Gemini Omni Flash 720p' },
  { id: 'google-veo-3.1-720p',       label: 'Veo 3.1 720p' },
  { id: 'google-veo-3.1-fast-720p',  label: 'Veo 3.1 Fast 720p' },
  { id: 'google-veo-3.1-lite-720p',  label: 'Veo 3.1 Lite 720p' },
  // ── 480p ──
  { id: 'grok-imagine',        label: 'Grok Imagine' },
  // ── 360p ──
  { id: 'google-omni-flash-360p',    label: 'Gemini Omni Flash 360p' },
];
const IMAGE_MODEL_REGISTRY = [
  // refs 2+ tier
  { id: 'google-nano-banana-pro', label: 'Nano Banana Pro' },
  { id: 'openai-image-2.5-sunburst', label: 'GPT Image 2.5 Sunburst' },
  { id: 'openai-image-2.5-flare-hq', label: 'GPT Image 2.5 Flare HQ' },
  { id: 'openai-image-2.5-flare',    label: 'GPT Image 2.5 Flare' },
  { id: 'google-nano-banana-2.1', label: 'Nano Banana 2.1' },
  { id: 'kling-3.0-omni-img',     label: 'Kling 3.0 Omni' },
  { id: 'bfl-flux-2-max',         label: 'FLUX.2 Max' },
  { id: 'bfl-flux-2-pro',         label: 'FLUX.2 Pro' },
  { id: 'bfl-flux-2-flex',        label: 'FLUX.2 Flex' },
  { id: 'kling-image-o1-img',     label: 'Kling Image O1' },
  { id: 'grok-imagine-image-2-hq',label: 'Grok Imagine 2.0 HQ' },
  { id: 'grok-imagine-image-2',   label: 'Grok Imagine 2.0' },
  { id: 'grok-imagine-image',     label: 'Grok Imagine' },
  { id: 'google-nano-banana-2-lite', label: 'Nano Banana 2 Lite' },
  { id: 'google-nano-banana',     label: 'Nano Banana' },
  { id: 'kling-2.1-multi-img',    label: 'Kling 2.1 Multi' },
  // single-ref / image-input tier
  { id: 'kling-3.0-img',          label: 'Kling 3.0' },
  { id: 'kling-2.1-img',          label: 'Kling 2.1' },
  { id: 'recraft-v3-vector-edit', label: 'Recraft v3 Vector Edit' },
  { id: 'recraft-vectorize',      label: 'Vectorize' },
  // text-only tier
  { id: 'recraft-v4-pro-vector',  label: 'Recraft v4 Pro Vector' },
  { id: 'recraft-v4-vector',      label: 'Recraft v4 Vector' },
  { id: 'recraft-v3-vector',      label: 'Recraft v3 Vector' },
];

const TABS = [
  { id: "appconfig", path: "app-config", label: "App Config", icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
  )},
  { id: "finances", path: "finances", label: "Finances", icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  )},
  { id: "jobqueue", path: "job-queue", label: "Job Queue", icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
  )},
  { id: "users", path: "users", label: "Users", icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  )},
];

function useAdminTab() {
  const location = useLocation();
  const seg = location.pathname.split("/")[2] || "";
  const match = TABS.find(t => t.path === seg);
  return match ? match.id : TABS[0].id;
}

// Returns the job ID segment when the URL is /admin/job-queue/<jobId>, or
// null when the user is on the queue itself. The queue tab renders the
// detail view in place when this is set.
function useAdminJobId() {
  const location = useLocation();
  const parts = location.pathname.split("/");
  if (parts[2] !== "job-queue") return null;
  return parts[3] || null;
}

export default function AdminView({ userRole }) {
  const navigate = useNavigate();
  const tab = useAdminTab();
  const jobDetailId = useAdminJobId();
  const setTab = (id) => {
    const t = TABS.find(x => x.id === id);
    navigate(`/admin/${t ? t.path : ""}`, { replace: true });
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("creator");
  const [adding, setAdding] = useState(false);
  // Studio keys state
  const [skLoading, setSkLoading] = useState(true);
  const [skHasKlingAccess, setSkHasKlingAccess] = useState(false);
  const [skHasKlingSecret, setSkHasKlingSecret] = useState(false);
  const [skHasGrokKey, setSkHasGrokKey] = useState(false);
  const [skHasGrokMgmt, setSkHasGrokMgmt] = useState(false);
  const [skGrokTeamId, setSkGrokTeamId] = useState(null);
  const [skKlingPreview, setSkKlingPreview] = useState(null);
  const [skKlingSecretPreview, setSkKlingSecretPreview] = useState(null);
  const [skGrokPreview, setSkGrokPreview] = useState(null);
  const [skGrokMgmtPreview, setSkGrokMgmtPreview] = useState(null);
  const [skAk, setSkAk] = useState("");
  const [skSk, setSkSk] = useState("");
  const [skGrokKey, setSkGrokKey] = useState("");
  const [skGrokMgmtKey, setSkGrokMgmtKey] = useState("");
  const [skGrokTeamIdInput, setSkGrokTeamIdInput] = useState("");
  const [skSaving, setSkSaving] = useState(false);
  const [skEditingAk, setSkEditingAk] = useState(false);
  const [skEditingSk, setSkEditingSk] = useState(false);
  const [skEditingGrok, setSkEditingGrok] = useState(false);
  const [skEditingGrokMgmt, setSkEditingGrokMgmt] = useState(false);
  const [skEditingTeamId, setSkEditingTeamId] = useState(false);
  const [skPrevTeamId, setSkPrevTeamId] = useState(null);
  // Gemini / Recraft / BFL studio keys (single Bearer/x-key each)
  const [skHasGemini, setSkHasGemini]   = useState(false);
  const [skHasRecraft, setSkHasRecraft] = useState(false);
  const [skHasBfl, setSkHasBfl]         = useState(false);
  const [skGeminiPreview, setSkGeminiPreview]   = useState(null);
  const [skRecraftPreview, setSkRecraftPreview] = useState(null);
  const [skBflPreview, setSkBflPreview]         = useState(null);
  const [skGeminiKey, setSkGeminiKey]   = useState("");
  const [skRecraftKey, setSkRecraftKey] = useState("");
  const [skBflKey, setSkBflKey]         = useState("");
  const [skEditingGemini, setSkEditingGemini]   = useState(false);
  const [skEditingRecraft, setSkEditingRecraft] = useState(false);
  const [skEditingBfl, setSkEditingBfl]         = useState(false);
  // Kling's newer API (3.0 Turbo) authenticates with a console API key
  const [skHasKlingApi, setSkHasKlingApi]         = useState(false);
  const [skKlingApiPreview, setSkKlingApiPreview] = useState(null);
  const [skKlingApiKey, setSkKlingApiKey]         = useState("");
  const [skEditingKlingApi, setSkEditingKlingApi] = useState(false);
  // OpenAI: API key for generation + optional admin key for the Costs API
  const [skHasOpenai, setSkHasOpenai]                 = useState(false);
  const [skHasOpenaiAdmin, setSkHasOpenaiAdmin]       = useState(false);
  const [skOpenaiPreview, setSkOpenaiPreview]         = useState(null);
  const [skOpenaiAdminPreview, setSkOpenaiAdminPreview] = useState(null);
  const [skOpenaiKey, setSkOpenaiKey]                 = useState("");
  const [skOpenaiAdminKey, setSkOpenaiAdminKey]       = useState("");
  const [skEditingOpenai, setSkEditingOpenai]         = useState(false);
  const [skEditingOpenaiAdmin, setSkEditingOpenaiAdmin] = useState(false);
  // Finances state
  const [finLoading, setFinLoading] = useState(true);
  const [finData, setFinData] = useState(null);
  const [finEditingKlingPts, setFinEditingKlingPts] = useState(false);
  const [finEditingKlingPPV, setFinEditingKlingPPV] = useState(false);
  const [finEditingKlingImgPts, setFinEditingKlingImgPts] = useState(false);
  const [finEditingKlingImgPPV, setFinEditingKlingImgPPV] = useState(false);
  const [finEditingGrokBal, setFinEditingGrokBal] = useState(false);
  const [finKlingPts, setFinKlingPts] = useState("");
  const [finKlingPPV, setFinKlingPPV] = useState("");
  const [finKlingImgPts, setFinKlingImgPts] = useState("");
  const [finKlingImgPPV, setFinKlingImgPPV] = useState("");
  const [finGrokBal, setFinGrokBal] = useState("");
  const [finSaving, setFinSaving] = useState(false);
  const [finGrokChecking, setFinGrokChecking] = useState(false);
  const [finEditingGoogleBal, setFinEditingGoogleBal] = useState(false);
  const [finGoogleBal, setFinGoogleBal] = useState("");
  const [finGoogleChecking, setFinGoogleChecking] = useState(false);
  const [finGoogleSpend, setFinGoogleSpend] = useState(null);
  const [finGoogleStatusChecking, setFinGoogleStatusChecking] = useState(false);
  const [finEditingOpenaiBal, setFinEditingOpenaiBal] = useState(false);
  const [finOpenaiBal, setFinOpenaiBal] = useState("");
  const [finOpenaiChecking, setFinOpenaiChecking] = useState(false);
  const [finEditingRecraftBal, setFinEditingRecraftBal] = useState(false);
  const [finRecraftBal, setFinRecraftBal] = useState("");
  const [finRecraftChecking, setFinRecraftChecking] = useState(false);
  const [finEditingBflBal, setFinEditingBflBal] = useState(false);
  const [finBflBal, setFinBflBal] = useState("");
  const [finBflChecking, setFinBflChecking] = useState(false);
  const [finArEditing, setFinArEditing] = useState(false);
  const [finAr, setFinAr] = useState({ enabled: false, threshold: "", amount: "", monthlyCap: "" });
  // App Config state
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgData, setCfgData] = useState(null);
  const [cfgMaxJobs, setCfgMaxJobs] = useState("");
  const [cfgStudioMax, setCfgStudioMax] = useState("");
  const [cfgStudioGrokRpm, setCfgStudioGrokRpm] = useState("");
  const [cfgImgMaxJobs, setCfgImgMaxJobs] = useState("");
  const [cfgImgStudioMax, setCfgImgStudioMax] = useState("");
  const [cfgImgStudioGrokRpm, setCfgImgStudioGrokRpm] = useState("");
  // Model Control state — sets of disabled / recommended model IDs.
  const [cfgVideoDisabled,    setCfgVideoDisabled]    = useState(new Set());
  const [cfgVideoRecommended, setCfgVideoRecommended] = useState(new Set());
  const [cfgImageDisabled,    setCfgImageDisabled]    = useState(new Set());
  const [cfgImageRecommended, setCfgImageRecommended] = useState(new Set());
  // Thumbnail backfill state
  const [thumbBackfill, setThumbBackfill] = useState(null);
  const [thumbBackfillKicking, setThumbBackfillKicking] = useState(false);
  // Grok cost audit state
  const [grokAudit, setGrokAudit] = useState(null);
  const [grokAuditLoading, setGrokAuditLoading] = useState(false);
  const [grokAuditError, setGrokAuditError] = useState(null);
  // Job Queue state
  const [queueData, setQueueData] = useState(null);
  const [recoveringId, setRecoveringId] = useState(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // email of user whose 3-dot menu is open
  const [menuPos, setMenuPos] = useState(null); // {top, right} for fixed-position menu
  const [giftDialog, setGiftDialog] = useState(null); // { uid, email } of user to gift credits
  const [giftAmount, setGiftAmount] = useState("");
  const [giftConfirming, setGiftConfirming] = useState(false); // true = showing confirmation step
  const [gifting, setGifting] = useState(false);
  const { notify } = useNotify();
  const confirm = useConfirm();
  const setHeaderRight = useHeaderRight();
  const refreshProfile = useRefreshProfile();
  // Re-fire setHeaderRight on theme toggle so the admin tab pills don't
  // hold stale T.* colors.
  const { mode: themeMode } = useTheme();

  const narrow = window.innerWidth <= 600;

  const loadUsers = () => {
    setLoading(true);
    api.getAdminUsers()
      .then(data => { setUsers(data.users || []); setLoading(false); })
      .catch(e => { notify("error", e.message); setLoading(false); });
  };

  const loadStudioKeys = () => {
    setSkLoading(true);
    api.getStudioKeys()
      .then(data => {
        setSkHasKlingAccess(data.hasKlingAccess || false);
        setSkHasKlingSecret(data.hasKlingSecret || false);
        setSkHasGrokKey(data.hasGrokKey || false);
        setSkHasGrokMgmt(data.hasGrokMgmt || false);
        setSkGrokTeamId(data.grokTeamId || null);
        setSkKlingPreview(data.klingAccessKeyPreview || null);
        setSkKlingSecretPreview(data.klingSecretKeyPreview || null);
        setSkGrokPreview(data.grokKeyPreview || null);
        setSkGrokMgmtPreview(data.grokMgmtKeyPreview || null);
        setSkHasGemini(data.hasGemini || false);
        setSkHasRecraft(data.hasRecraft || false);
        setSkHasBfl(data.hasBfl || false);
        setSkGeminiPreview(data.geminiKeyPreview || null);
        setSkRecraftPreview(data.recraftKeyPreview || null);
        setSkBflPreview(data.bflKeyPreview || null);
        setSkHasKlingApi(data.hasKlingApi || false);
        setSkKlingApiPreview(data.klingApiKeyPreview || null);
        setSkHasOpenai(data.hasOpenai || false);
        setSkHasOpenaiAdmin(data.hasOpenaiAdmin || false);
        setSkOpenaiPreview(data.openaiKeyPreview || null);
        setSkOpenaiAdminPreview(data.openaiAdminKeyPreview || null);
        setSkLoading(false);
      })
      .catch(e => { notify("error", e.message); setSkLoading(false); });
  };

  const loadFinances = () => {
    setFinLoading(true);
    api.getFinances()
      .then(data => { setFinData(data); setFinLoading(false); })
      .catch(e => { notify("error", e.message); setFinLoading(false); });
  };

  const loadAppConfig = () => {
    setCfgLoading(true);
    api.getAppConfig()
      .then(data => {
        setCfgData(data);
        setCfgMaxJobs(String(data.maxConcurrentJobs ?? 2));
        setCfgStudioMax(String(data.studioKlingConcurrentMax ?? 20));
        setCfgStudioGrokRpm(String(data.studioGrokRpmMax ?? 60));
        setCfgImgMaxJobs(String(data.imageMaxConcurrentJobs ?? data.maxConcurrentJobs ?? 2));
        setCfgImgStudioMax(String(data.studioKlingImageConcurrentMax ?? data.studioKlingConcurrentMax ?? 20));
        setCfgImgStudioGrokRpm(String(data.studioGrokImageRpmMax ?? data.studioGrokRpmMax ?? 60));
        setCfgVideoDisabled(new Set(data.videoModelDisabled || []));
        setCfgVideoRecommended(new Set(data.videoModelRecommended || []));
        setCfgImageDisabled(new Set(data.imageModelDisabled || []));
        setCfgImageRecommended(new Set(data.imageModelRecommended || []));
        setCfgLoading(false);
      })
      .catch(e => { notify("error", e.message); setCfgLoading(false); });
  };

  useEffect(() => { loadUsers(); loadStudioKeys(); loadFinances(); loadAppConfig(); }, []);

  // Render the admin tab switcher into the page header. Positioned
  // absolutely against the page header (the nearest position:fixed
  // ancestor) so it centers across the full header width rather than
  // the headerRight slot, and sits flush with the bottom edge so the
  // active tab's yellow underline meets the header's bottom border -
  // same visual as the Video/Image tabs on the Generate page.
  useEffect(() => {
    setHeaderRight(
      <div style={{
        position:"absolute",
        left:"50%",
        bottom:0,
        transform:"translateX(-50%)",
        display:"flex",
        gap:0,
        maxWidth:"calc(100% - 24px)",
        overflowX:"auto",
      }}>
        {TABS.map(t => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              title={t.label}
              style={{
                background:"transparent",
                border:"none",
                padding:"10px 18px",
                cursor:"pointer",
                borderBottom:`2px solid ${active ? Y : "transparent"}`,
                color: active ? T.title : `${T.title}99`,
                transition:"color 0.15s, border-color 0.15s",
                display:"inline-flex",
                alignItems:"center",
                flexShrink:0,
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = T.title; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = `${T.title}99`; }}
            >
              {React.cloneElement(t.icon, { width: 22, height: 22 })}
            </button>
          );
        })}
      </div>
    );
    return () => setHeaderRight(null);
  }, [tab, themeMode]);

  const handleAdd = async () => {
    if (!newEmail || !newEmail.includes("@")) return;
    setAdding(true);
    try {
      await api.addAdminUser(newEmail.toLowerCase().trim(), newRole);
      setNewEmail("");
      setNewRole("creator");
      notify("success", "User added");
      loadUsers();
    } catch (e) {
      notify("error", e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRoleChange = async (email, newRole) => {
    try {
      await api.updateAdminUser(email, { role: newRole });
      // Optimistically update local state immediately
      setUsers(prev => prev.map(u => u.email === email ? { ...u, role: newRole } : u));
      notify("success", `${email} is now ${newRole}`);
    } catch (e) {
      notify("error", e.message);
      loadUsers(); // reload on error to revert
    }
  };

  const handleToggleActive = async (email, currentActive, role) => {
    if (role === "owner") return;
    try {
      await api.updateAdminUser(email, { active: !currentActive });
      notify("success", currentActive ? "User deactivated" : "User activated");
      loadUsers();
    } catch (e) {
      notify("error", e.message);
    }
  };

  const handleDelete = async (email, role) => {
    if (role === "owner") return;
    const ok = await confirm({
      title: "Remove user permanently?",
      message: `${email} will be permanently removed and will no longer be able to log in.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteAdminUser(email);
      notify("success", "User removed");
      loadUsers();
    } catch (e) {
      notify("error", e.message);
    }
  };

  const handleGiftCredits = async () => {
    if (!giftDialog || !giftAmount) return;
    const amount = parseFloat(giftAmount);
    if (!amount || amount <= 0) { notify("error", "Enter a valid amount"); return; }
    setGifting(true);
    try {
      await api.giftCredits(giftDialog.uid, amount, giftDialog.email);
      notify("success", `Gifted ${amount.toLocaleString()} credits to ${giftDialog.email}`);
      setGiftDialog(null);
      setGiftAmount("");
      setGiftConfirming(false);
      loadUsers();
      // Refresh the header balance pill too, so gifting yourself reflects
      // immediately without a reload.
      refreshProfile?.();
    } catch (e) {
      notify("error", e.message);
    } finally {
      setGifting(false);
    }
  };

  // ─── Users content ──────────────────────────────────────────────────────────
  const renderUsers = () => (
    <>
      <PageLoader ready={!loading}/>
      {/* Add user form */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:28,flexWrap:"wrap"}}>
        <input
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder="email@example.com"
          onKeyDown={e => e.key === "Enter" && handleAdd()}
          style={{
            flex: 1, minWidth: 200, padding: "10px 14px", borderRadius: 10,
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
            color: T.text, fontSize: 13, outline: "none", fontFamily: "inherit",
          }}
        />
        <select
          value={newRole}
          onChange={e => setNewRole(e.target.value)}
          style={{
            padding: "10px 14px", borderRadius: 10,
            background: T.surface2, border: "1px solid rgba(255,255,255,0.1)",
            color: T.text, fontSize: 12, outline: "none", cursor: "pointer",
          }}
        >
          <option value="admin">Admin</option>
          <option value="creator">Creator</option>
          <option value="viewer">Viewer</option>
        </select>
        <button
          onClick={handleAdd}
          disabled={!newEmail || !newEmail.includes("@") || adding}
          style={{
            padding: "10px 20px", borderRadius: 10,
            background: newEmail && newEmail.includes("@") ? Y : "rgba(255,255,255,0.06)",
            border: "none",
            color: newEmail && newEmail.includes("@") ? T.dark : T.text3,
            fontWeight: 700, fontSize: 12, cursor: newEmail && newEmail.includes("@") ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add
        </button>
      </div>

      {/* Scrollable table wrapper for mobile */}
      <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch",position:"relative"}}>
      {/* Column headers */}
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 0 6px",borderBottom:"1px solid rgba(255,255,255,0.08)",minWidth:narrow?560:"auto"}}>
        <div style={{flexShrink:0,width:20}}/>
        <div style={{flex:1,minWidth:0,color:T.text3,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>User</div>
        <div style={{flexShrink:0,width:100,color:T.text3,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",textAlign:"center"}}>Role</div>
        <div style={{flexShrink:0,width:30,color:T.text3,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",textAlign:"right"}}>Gens</div>
        <div style={{flexShrink:0,width:70,color:T.text3,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",textAlign:"right"}}>Credits</div>
        <div style={{flexShrink:0,width:70,color:T.text3,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",textAlign:"center"}}>Last Login</div>
        <div style={{flexShrink:0,width:28}}/>
      </div>

      {/* User list */}
      <div style={{display:"flex",flexDirection:"column",gap:0}}>
        {users.map(u => (
          <div key={u.email} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",opacity:u.active?1:0.4,transition:"opacity 0.2s",minWidth:narrow?560:"auto"}}>
            {/* User status icon */}
            <div style={{flexShrink:0,width:20,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={(u.role === "owner" || u.role === "admin") ? ROLE_COLORS[u.role] : T.text3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            {/* Email + DA username */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:u.active?T.text:T.text3,fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {u.email}
              </div>
              {u.daUsername && (
                <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#05CC47"><path d="M18 3h-4l-1 2-1.8 3H6v5h3.4L6 20h4l1-2 1.8-3H18v-5h-3.4L18 3z"/></svg>
                  <span style={{color:T.text3,fontSize:10}}>{u.daUsername}</span>
                </div>
              )}
              {u.discordUsername && (
                <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.074-.035 19.74 19.74 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
                  <span style={{color:T.text3,fontSize:10}}>{u.discordUsername}</span>
                </div>
              )}
            </div>
            {/* Role badge — dropdown for non-owners */}
            <div style={{flexShrink:0,width:100,display:"flex",justifyContent:"center"}}>
              <RoleBadge role={u.role} editable={u.role !== "owner"} onChangeRole={(newRole) => handleRoleChange(u.email, newRole)}/>
            </div>
            {/* Generation count */}
            <div style={{flexShrink:0,width:30,color:T.text3,fontSize:11,textAlign:"right"}}>
              {u.generationCount}
            </div>
            {/* Credits balance — always shown in turquoise */}
            <div style={{flexShrink:0,width:70,textAlign:"right",fontSize:11,fontWeight:(u.creditBalance||u.eddyBalance||0)>0?700:400,color:CREDIT,opacity:(u.creditBalance||u.eddyBalance||0)>0?1:0.35}}>
              {Math.round(u.creditBalance ?? u.eddyBalance ?? 0).toLocaleString()}
            </div>
            {/* Last login */}
            <div style={{flexShrink:0,width:70,color:T.text3,fontSize:11,textAlign:"center"}}>
              {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : "\u2014"}
            </div>
            {/* 3-dot menu — sticky right on mobile */}
            <div style={{flexShrink:0,width:28,position:narrow?"sticky":"relative",right:narrow?0:undefined,background:narrow?T.dark:undefined,zIndex:narrow?10:undefined}}>
              {/* Owners can gift any account (incl. other owners + self),
                  so show the menu for owner rows too - but only the Gift
                  action; deactivate / remove stay hidden for owners. */}
              {(userRole === "owner" || u.role !== "owner") && (
                <>
                  <button onClick={(e) => {
                      e.stopPropagation();
                      if (openMenu === u.email) { setOpenMenu(null); setMenuPos(null); return; }
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPos({ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) });
                      setOpenMenu(u.email);
                    }}
                    style={{background:"transparent",border:"none",padding:4,color:T.text3,cursor:"pointer",display:"flex",transition:"color 0.15s"}}
                    onMouseEnter={e=>e.currentTarget.style.color=T.text} onMouseLeave={e=>e.currentTarget.style.color=T.text3}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                  </button>
                  {openMenu === u.email && menuPos && (
                    <>
                      {/* Click-away overlay */}
                      <div onClick={() => { setOpenMenu(null); setMenuPos(null); }} style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:99}}/>
                      <div style={{
                        position:"fixed",right:menuPos.right,top:menuPos.top,zIndex:100,
                        background:T.surface2,border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:10,padding:4,minWidth:180,
                        boxShadow:"0 8px 32px rgba(0,0,0,0.4)",
                      }}>
                        {/* Gift credits — owner only */}
                        {userRole === "owner" && (
                          <button onClick={() => { setGiftDialog({ uid: u.uid || null, email: u.email }); setGiftAmount(""); setOpenMenu(null); }}
                            style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 12px",background:"transparent",border:"none",color:CREDIT,fontSize:12,cursor:"pointer",borderRadius:6,transition:"background 0.15s"}}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6"/><polyline points="12 3 12 15"/><path d="M8 7l4-4 4 4"/></svg>
                            Gift credits
                          </button>
                        )}
                        {/* Activate / Deactivate / Remove — never for owner
                            accounts (protected; owners only get Gift above).
                            Divider only shows when Gift is also present. */}
                        {u.role !== "owner" && (
                          <>
                            {userRole === "owner" && <div style={{height:1,background:"rgba(255,255,255,0.06)",margin:"4px 0"}}/>}
                            <button onClick={() => { handleToggleActive(u.email, u.active, u.role); setOpenMenu(null); }}
                              style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 12px",background:"transparent",border:"none",color:u.active?Y:T.success,fontSize:12,cursor:"pointer",borderRadius:6,transition:"background 0.15s"}}
                              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              {u.active
                                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                              {u.active ? "Deactivate" : "Activate"}
                            </button>
                            <button onClick={() => { handleDelete(u.email, u.role); setOpenMenu(null); }}
                              style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"8px 12px",background:"transparent",border:"none",color:T.danger,fontSize:12,cursor:"pointer",borderRadius:6,transition:"background 0.15s"}}
                              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                              Remove user
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>{/* end scrollable wrapper */}
    </>
  );

  // ─── Studio Keys content ────────────────────────────────────────────────────
  const skKeyRow = (label, icon, hasKey, keyPreview, inputVal, setInputVal, onSave, onEdit, onDelete, saving, onCancel) => (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"13px 0",borderBottom:hasKey ? "1px solid rgba(255,255,255,0.06)" : "none"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text2,fontSize:11,minWidth:60}}>
        {icon}
        {label}
      </div>
      {hasKey ? (
        <>
          <span style={{flex:1,color:T.text3,fontSize:13,fontFamily:"monospace"}}>{keyPreview || "\u2022".repeat(12)}</span>
          <button onClick={onEdit} title={`Edit ${label}`}
            className="a-edit" style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",flexShrink:0}}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button onClick={onDelete} title={`Remove ${label}`}
            className="a-del" style={{background:"transparent",border:"none",color:T.danger,cursor:"pointer",padding:2,display:"flex",flexShrink:0}}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
          </button>
        </>
      ) : (
        <div style={{flex:1,display:"flex",gap:8,alignItems:"center"}}>
          <div style={{flex:1}}>
            <input value={inputVal} onChange={e=>setInputVal(e.target.value)} placeholder={`Paste ${label}`} type="password"
              style={{...inp,marginBottom:0,fontSize:13,fontFamily:"monospace",color:T.title,borderBottom:`1px solid ${inputVal ? T.title : "rgba(255,255,255,0.15)"}`,transition:"border-color 0.2s"}}
              onKeyDown={e => { if (e.key === "Enter" && inputVal) onSave(); }}/>
          </div>
          {inputVal && (
            <button onClick={onSave} disabled={saving}
              style={{padding:"8px 20px",borderRadius:16,background:Y,border:"none",color:T.dark,fontWeight:700,fontSize:12,cursor:saving?"not-allowed":"pointer",display:"inline-flex",alignItems:"center",gap:6,opacity:saving?0.7:1,flexShrink:0}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {saving ? "Saving\u2026" : ""}
            </button>
          )}
          {onCancel && (
            <button onClick={onCancel} title="Cancel"
              className="a-cancel" style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",flexShrink:0}}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
      )}
    </div>
  );

  const handleSkSaveKling = async () => {
    if (!skAk && !skHasKlingAccess) return;
    if (!skSk && !skHasKlingSecret) return;
    setSkSaving(true);
    try {
      const keys = {};
      if (skAk) keys.klingAccessKey = skAk;
      if (skSk) keys.klingSecretKey = skSk;
      const res = await api.saveStudioKeys(keys);
      if (res.klingAccessKeyPreview) { setSkHasKlingAccess(true); setSkKlingPreview(res.klingAccessKeyPreview); }
      if (res.klingSecretKeyPreview) { setSkHasKlingSecret(true); setSkKlingSecretPreview(res.klingSecretKeyPreview); }
      setSkAk(""); setSkSk("");
      notify("success", "Studio Kling keys saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveGrok = async () => {
    if (!skGrokKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ grokApiKey: skGrokKey });
      if (res.grokApiKeyPreview) { setSkHasGrokKey(true); setSkGrokPreview(res.grokApiKeyPreview); }
      setSkGrokKey("");
      notify("success", "Studio Grok key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveGrokMgmt = async () => {
    if (!skGrokMgmtKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ grokMgmtKey: skGrokMgmtKey });
      if (res.grokMgmtKeyPreview) { setSkHasGrokMgmt(true); setSkGrokMgmtPreview(res.grokMgmtKeyPreview); }
      setSkGrokMgmtKey("");
      notify("success", "Studio Grok management key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveGemini = async () => {
    if (!skGeminiKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ geminiApiKey: skGeminiKey });
      if (res.geminiApiKeyPreview) { setSkHasGemini(true); setSkGeminiPreview(res.geminiApiKeyPreview); }
      setSkGeminiKey(""); setSkEditingGemini(false);
      notify("success", "Studio Gemini key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveRecraft = async () => {
    if (!skRecraftKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ recraftApiKey: skRecraftKey });
      if (res.recraftApiKeyPreview) { setSkHasRecraft(true); setSkRecraftPreview(res.recraftApiKeyPreview); }
      setSkRecraftKey(""); setSkEditingRecraft(false);
      notify("success", "Studio Recraft key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveBfl = async () => {
    if (!skBflKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ bflApiKey: skBflKey });
      if (res.bflApiKeyPreview) { setSkHasBfl(true); setSkBflPreview(res.bflApiKeyPreview); }
      setSkBflKey(""); setSkEditingBfl(false);
      notify("success", "Studio BFL FLUX key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveKlingApi = async () => {
    if (!skKlingApiKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ klingApiKey: skKlingApiKey });
      if (res.klingApiKeyPreview) { setSkHasKlingApi(true); setSkKlingApiPreview(res.klingApiKeyPreview); }
      setSkKlingApiKey(""); setSkEditingKlingApi(false);
      notify("success", "Studio Kling API key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveOpenai = async () => {
    if (!skOpenaiKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ openaiApiKey: skOpenaiKey });
      if (res.openaiApiKeyPreview) { setSkHasOpenai(true); setSkOpenaiPreview(res.openaiApiKeyPreview); }
      setSkOpenaiKey(""); setSkEditingOpenai(false);
      notify("success", "Studio OpenAI key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveOpenaiAdmin = async () => {
    if (!skOpenaiAdminKey) return;
    setSkSaving(true);
    try {
      const res = await api.saveStudioKeys({ openaiAdminKey: skOpenaiAdminKey });
      if (res.openaiAdminKeyPreview) { setSkHasOpenaiAdmin(true); setSkOpenaiAdminPreview(res.openaiAdminKeyPreview); }
      setSkOpenaiAdminKey(""); setSkEditingOpenaiAdmin(false);
      notify("success", "Studio OpenAI admin key saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleSkSaveGrokTeamId = async () => {
    if (!skGrokTeamIdInput) return;
    setSkSaving(true);
    try {
      await api.saveStudioKeys({ grokTeamId: skGrokTeamIdInput });
      setSkGrokTeamId(skGrokTeamIdInput);
      setSkGrokTeamIdInput("");
      notify("success", "Studio Grok Team ID saved");
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const handleCopyMyKeys = async () => {
    const ok = await confirm({
      title: "Copy your personal keys?",
      message: "This will copy all your personal API keys (Kling, Grok) into the Studio key store, encrypting them at rest.",
      confirmLabel: "Copy Keys",
    });
    if (!ok) return;
    setSkSaving(true);
    try {
      const res = await api.copyMyKeysToStudio();
      notify("success", `Copied ${res.copied?.length || 0} keys to studio store`);
      loadStudioKeys();
    } catch (e) { notify("error", e.message); }
    finally { setSkSaving(false); }
  };

  const renderStudioKeys = () => (
    <div>
      {/* ══════════ Kling ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <div style={{width:25,height:25,borderRadius:5,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <img src="/kling-logo.png" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Kling"/>
            </div>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Kling</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("Access",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasKlingAccess, skKlingPreview, skAk, setSkAk,
            handleSkSaveKling,
            () => { setSkEditingAk(true); setSkHasKlingAccess(false); setSkAk(""); },
            async () => { try { await api.deleteStudioKey("klingAccess"); setSkHasKlingAccess(false); setSkKlingPreview(null); notify("success","Access key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingAk ? () => { setSkHasKlingAccess(true); setSkAk(""); setSkEditingAk(false); } : null
          )}
          {skKeyRow("Secret",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z"/></svg>,
            skHasKlingSecret, skKlingSecretPreview, skSk, setSkSk,
            handleSkSaveKling,
            () => { setSkEditingSk(true); setSkHasKlingSecret(false); setSkSk(""); },
            async () => { try { await api.deleteStudioKey("klingSecret"); setSkHasKlingSecret(false); setSkKlingSecretPreview(null); notify("success","Secret key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingSk ? () => { setSkHasKlingSecret(true); setSkSk(""); setSkEditingSk(false); } : null
          )}
          {/* Save button when both need entry */}
          {(!skHasKlingAccess || !skHasKlingSecret) && (skAk || skSk) && (skAk || skHasKlingAccess) && (skSk || skHasKlingSecret) && (
            <button onClick={handleSkSaveKling} disabled={skSaving}
              style={{marginTop:12,padding:"8px 20px",borderRadius:16,background:Y,border:"none",color:T.dark,fontWeight:700,fontSize:12,cursor:skSaving?"not-allowed":"pointer",display:"inline-flex",alignItems:"center",gap:6,opacity:skSaving?0.7:1}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              {skSaving ? "Saving\u2026" : "Save Kling Keys"}
            </button>
          )}
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasKlingApi, skKlingApiPreview, skKlingApiKey, setSkKlingApiKey,
            handleSkSaveKlingApi,
            () => { setSkEditingKlingApi(true); setSkHasKlingApi(false); setSkKlingApiKey(""); },
            async () => { try { await api.deleteStudioKey("klingApi"); setSkHasKlingApi(false); setSkKlingApiPreview(null); notify("success","Kling API key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingKlingApi ? () => { setSkHasKlingApi(true); setSkKlingApiKey(""); setSkEditingKlingApi(false); } : null
          )}
          <div style={{color:T.text3,fontSize:11,marginTop:8,lineHeight:1.5}}>
            Access / Secret keys run the legacy Kling API. The API key (Kling console, "+ Create a new API Key") runs Kling's newer API, used by Kling 3.0 Turbo.
          </div>
        </div>
      </div>

      {/* ══════════ Grok ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <svg width="24" height="24" viewBox="0 0 512 510" fill="#fff"><path d="M115.6 0h280.8C459.97 0 512 52 512 115.6v278.4c0 63.6-52 115.6-115.6 115.6H115.6C52 509.6 0 457.6 0 394V115.6C0 52 52 0 115.6 0z" fill="#000"/><path d="M213.2 306l179-180v.2l51.7-51.8c-.9 1.3-1.9 2.6-2.8 3.9-39.3 54.2-58.5 80.6-43.1 146.9l-.1-.1c10.6 45.1-.7 95.1-37.4 131.8-46.2 46.3-120.2 56.6-181.1 14.9l42.5-19.7c38.9 15.3 81.4 8.6 111.9-22 30.6-30.6 37.4-75.2 22.1-112.3-2.9-7-11.7-8.8-17.8-4.3L213.2 306zm-25.8 22.4l-.1.1L68.1 435.2c7.6-10.4 17-20.3 26.3-30.1 26.4-27.8 52.7-55.4 36.7-94.3-21.4-52.1-9-113.2 30.7-152.9 41.2-41.3 102-51.7 152.7-30.8 11.2 4.2 21 10.1 28.6 15.6l-42.4 19.6c-39.4-16.6-84.6-5.3-112.2 22.3-37.3 37.3-44.8 102-1.1 143.8z" fill="#fff"/></svg>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Grok</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasGrokKey, skGrokPreview, skGrokKey, setSkGrokKey,
            handleSkSaveGrok,
            () => { setSkEditingGrok(true); setSkHasGrokKey(false); setSkGrokKey(""); },
            async () => { try { await api.deleteStudioKey("grokApi"); setSkHasGrokKey(false); setSkGrokPreview(null); notify("success","Grok key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingGrok ? () => { setSkHasGrokKey(true); setSkGrokKey(""); setSkEditingGrok(false); } : null
          )}
          {skKeyRow("Mgmt Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>,
            skHasGrokMgmt, skGrokMgmtPreview, skGrokMgmtKey, setSkGrokMgmtKey,
            handleSkSaveGrokMgmt,
            () => { setSkEditingGrokMgmt(true); setSkHasGrokMgmt(false); setSkGrokMgmtKey(""); },
            async () => { try { await api.deleteStudioKey("grokMgmt"); setSkHasGrokMgmt(false); setSkGrokMgmtPreview(null); notify("success","Grok management key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingGrokMgmt ? () => { setSkHasGrokMgmt(true); setSkGrokMgmtKey(""); setSkEditingGrokMgmt(false); } : null
          )}
          {/* Team ID — plain text, not encrypted */}
          {skKeyRow("Team ID",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>,
            !!skGrokTeamId, skGrokTeamId, skGrokTeamIdInput, setSkGrokTeamIdInput,
            handleSkSaveGrokTeamId,
            () => { setSkPrevTeamId(skGrokTeamId); setSkEditingTeamId(true); setSkGrokTeamId(null); setSkGrokTeamIdInput(""); },
            async () => { try { await api.deleteStudioKey("grokTeamId"); setSkGrokTeamId(null); notify("success","Grok Team ID removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingTeamId ? () => { setSkGrokTeamId(skPrevTeamId); setSkGrokTeamIdInput(""); setSkEditingTeamId(false); } : null
          )}
        </div>
      </div>

      {/* ══════════ Gemini ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <ModelIcon model="google-nano-banana-pro" s={26}/>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Gemini</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasGemini, skGeminiPreview, skGeminiKey, setSkGeminiKey,
            handleSkSaveGemini,
            () => { setSkEditingGemini(true); setSkHasGemini(false); setSkGeminiKey(""); },
            async () => { try { await api.deleteStudioKey("gemini"); setSkHasGemini(false); setSkGeminiPreview(null); notify("success","Gemini key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingGemini ? () => { setSkHasGemini(true); setSkGeminiKey(""); setSkEditingGemini(false); } : null
          )}
          <div style={{color:T.text3,fontSize:11,marginTop:8,lineHeight:1.5}}>
            Used for Imagen, Nano Banana, and Veo. Falls back to the deployment key if unset.
          </div>
        </div>
      </div>

      {/* ══════════ Recraft ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <ModelIcon model="recraft-v4-vector" s={26}/>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>Recraft</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasRecraft, skRecraftPreview, skRecraftKey, setSkRecraftKey,
            handleSkSaveRecraft,
            () => { setSkEditingRecraft(true); setSkHasRecraft(false); setSkRecraftKey(""); },
            async () => { try { await api.deleteStudioKey("recraft"); setSkHasRecraft(false); setSkRecraftPreview(null); notify("success","Recraft key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingRecraft ? () => { setSkHasRecraft(true); setSkRecraftKey(""); setSkEditingRecraft(false); } : null
          )}
          <div style={{color:T.text3,fontSize:11,marginTop:8,lineHeight:1.5}}>
            Used for Recraft vector / vectorize models. Falls back to the deployment key if unset.
          </div>
        </div>
      </div>

      {/* ══════════ BFL FLUX ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <ModelIcon model="bfl-flux-2-pro" s={26}/>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>BFL FLUX</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasBfl, skBflPreview, skBflKey, setSkBflKey,
            handleSkSaveBfl,
            () => { setSkEditingBfl(true); setSkHasBfl(false); setSkBflKey(""); },
            async () => { try { await api.deleteStudioKey("bfl"); setSkHasBfl(false); setSkBflPreview(null); notify("success","BFL FLUX key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingBfl ? () => { setSkHasBfl(true); setSkBflKey(""); setSkEditingBfl(false); } : null
          )}
          <div style={{color:T.text3,fontSize:11,marginTop:8,lineHeight:1.5}}>
            Used for FLUX.2 image models (x-key auth). Falls back to the deployment key if unset.
          </div>
        </div>
      </div>

      {/* ══════════ OpenAI ══════════ */}
      <div style={{marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:36,height:36,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <ModelIcon model="openai-image-2.5-flare" s={26}/>
          </div>
          <span style={{color:T.text2,fontSize:26,fontWeight:800,letterSpacing:"0.02em"}}>OpenAI</span>
        </div>
        <div style={{paddingLeft:16}}>
          {skKeyRow("API Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M7 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm0-8C3.69 6 1 8.69 1 12s2.69 6 6 6c2.22 0 4.15-1.21 5.19-3H17v2h2v-2h2v-4H12.19C11.15 8.21 9.22 6 7 6z"/></svg>,
            skHasOpenai, skOpenaiPreview, skOpenaiKey, setSkOpenaiKey,
            handleSkSaveOpenai,
            () => { setSkEditingOpenai(true); setSkHasOpenai(false); setSkOpenaiKey(""); },
            async () => { try { await api.deleteStudioKey("openai"); setSkHasOpenai(false); setSkOpenaiPreview(null); notify("success","OpenAI key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingOpenai ? () => { setSkHasOpenai(true); setSkOpenaiKey(""); setSkEditingOpenai(false); } : null
          )}
          {skKeyRow("Admin Key",
            <svg width="16" height="16" viewBox="0 0 24 24" fill={T.text3}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>,
            skHasOpenaiAdmin, skOpenaiAdminPreview, skOpenaiAdminKey, setSkOpenaiAdminKey,
            handleSkSaveOpenaiAdmin,
            () => { setSkEditingOpenaiAdmin(true); setSkHasOpenaiAdmin(false); setSkOpenaiAdminKey(""); },
            async () => { try { await api.deleteStudioKey("openaiAdmin"); setSkHasOpenaiAdmin(false); setSkOpenaiAdminPreview(null); notify("success","OpenAI admin key removed"); } catch(e) { notify("error",e.message); } },
            skSaving,
            skEditingOpenaiAdmin ? () => { setSkHasOpenaiAdmin(true); setSkOpenaiAdminKey(""); setSkEditingOpenaiAdmin(false); } : null
          )}
          <div style={{color:T.text3,fontSize:11,marginTop:8,lineHeight:1.5}}>
            The API key runs GPT Image 2.5 (falls back to the deployment key if unset). The optional admin key (sk-admin-...) lets Finances read actual spend from OpenAI's Costs API.
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Finances ────────────────────────────────────────────────────────────────
  const handleFinSave = async (field, value) => {
    setFinSaving(true);
    try {
      await api.saveFinances({ [field]: Number(value) });
      notify("success", "Updated");
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinSaving(false);
    setFinEditingKlingPts(false);
    setFinEditingKlingPPV(false);
    setFinEditingGrokBal(false);
    setFinEditingGoogleBal(false);
    setFinEditingOpenaiBal(false);
    setFinEditingRecraftBal(false);
    setFinEditingBflBal(false);
  };

  // Recraft and BFL report their live prepaid balance, so Auto-update
  // replaces the ledger with the real number (like Grok).
  const handleRecraftAutoUpdate = async () => {
    setFinRecraftChecking(true);
    try {
      const data = await api.checkStudioRecraftBalance();
      notify("success", `Recraft balance updated: $${data.balance.toFixed(2)}`);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinRecraftChecking(false);
  };

  const handleBflAutoUpdate = async () => {
    setFinBflChecking(true);
    try {
      const data = await api.checkStudioBflBalance();
      notify("success", `BFL balance updated: $${data.balance.toFixed(2)}`);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinBflChecking(false);
  };

  const handleOpenAISpendCheck = async () => {
    setFinOpenaiChecking(true);
    try {
      const data = await api.checkStudioOpenAISpend(30);
      notify("success", `OpenAI last 30d spend: $${data.totalUsd.toFixed(2)}`);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinOpenaiChecking(false);
  };

  const handleGrokAutoUpdate = async () => {
    setFinGrokChecking(true);
    try {
      const data = await api.checkStudioGrokBalance();
      notify("success", `Grok balance updated: $${data.balance.toFixed(2)}`);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinGrokChecking(false);
  };

  const handleGoogleSpendCheck = async () => {
    setFinGoogleChecking(true);
    try {
      const data = await api.checkStudioGoogleSpend(30);
      setFinGoogleSpend(data);
      notify("success", `Gemini last 30d spend: $${data.totalCostUsd.toFixed(2)} across ${data.jobsWithCost} jobs`);
    } catch (e) { notify("error", e.message); }
    setFinGoogleChecking(false);
  };

  // The Gemini API has no balance endpoint; this asks whether prepaid
  // credits are above $0 (a 1-token call that 402s when depleted).
  const handleGoogleStatusCheck = async () => {
    setFinGoogleStatusChecking(true);
    try {
      const data = await api.checkStudioGoogleBilling();
      if (data.status === "active") notify("success", "Gemini credits are active");
      else if (data.status === "depleted") notify("error", "Gemini prepaid credits are depleted - top up in AI Studio");
      else notify("error", `Gemini status check failed: ${data.detail || "unknown error"}`);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinGoogleStatusChecking(false);
  };

  const handleGoogleAutoReloadSave = async () => {
    setFinSaving(true);
    try {
      await api.saveFinances({ googleAutoReload: {
        enabled: finAr.enabled,
        threshold: Number(finAr.threshold) || 0,
        amount: Number(finAr.amount) || 0,
        monthlyCap: Number(finAr.monthlyCap) || 0,
      }});
      notify("success", "Auto-reload settings saved");
      setFinArEditing(false);
      loadFinances();
    } catch (e) { notify("error", e.message); }
    setFinSaving(false);
  };

  const renderFinances = () => {
    const d = finData || {};
    const klingPts = d.klingPoints ?? 0;
    const klingPPV = d.klingPointValue ?? 0.14;
    const klingUsd = d.klingCoverageUsd ?? 0;
    const klingImgPts = d.klingImagePoints ?? 0;
    const klingImgPPV = d.klingImagePointValue ?? 0.0035;
    const klingImgUsd = d.klingImageCoverageUsd ?? 0;
    const grokBal = d.grokBalance ?? 0;
    const googleBal = d.googleBalance ?? 0;
    const openaiBal = d.openaiBalance ?? 0;
    const recraftBal = d.recraftBalance ?? 0;
    const bflBal = d.bflBalance ?? 0;
    const googleStatus = d.googleBillingStatus || null;
    const googleAr = d.googleAutoReload || { enabled: false, threshold: 0, amount: 0, monthlyCap: 0 };
    const fmtWhen = (iso) => iso
      ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "never";
    const totalCredits = d.totalCredits ?? d.totalEddies ?? 0;
    const totalCreditsUsd = d.totalCreditsUsd ?? (totalCredits * 0.002);
    const studioUsd = d.studioCoverageUsd ?? 0;
    const deficit = d.deficit ?? 0;
    const hasDeficit = deficit > 0;
    const hasSurplus = deficit < 0;

    const finRow = (label, icon, value, color, editState, setEditState, inputVal, setInputVal, field, opts = {}) => (
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 0",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
          background:`${color}12`}}>
          {icon}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:T.text3,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
          {editState ? (
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
              <input type="number" step="any" value={inputVal} onChange={e => setInputVal(e.target.value)}
                autoFocus
                style={{padding:"6px 10px",borderRadius:8,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",
                  color:T.text,fontSize:14,fontWeight:700,width:140,outline:"none",fontFamily:"inherit"}}
              />
              <button onClick={() => handleFinSave(field, inputVal)} disabled={finSaving}
                style={{padding:"4px 12px",borderRadius:8,background:CREDIT,border:"none",color:T.dark,fontSize:11,fontWeight:700,cursor:"pointer"}}>
                Save
              </button>
              <button onClick={() => setEditState(false)}
                style={{padding:"4px 10px",borderRadius:8,background:"rgba(255,255,255,0.06)",border:"none",color:T.text3,fontSize:11,cursor:"pointer"}}>
                ✕
              </button>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:2}}>
              <div style={{color,fontSize:18,fontWeight:800,cursor:"pointer"}}
                onClick={() => { setEditState(true); setInputVal(typeof value === 'number' ? String(value) : value); }}>
                {typeof value === 'number' ? value.toLocaleString(undefined, {minimumFractionDigits: opts.decimals ?? 2, maximumFractionDigits: opts.decimals ?? 2}) : value}
                <span style={{marginLeft:6,opacity:0.4,fontSize:10,fontWeight:500}}>✎</span>
              </div>
              {opts.autoUpdate && (
                <button onClick={opts.autoUpdate} disabled={opts.autoUpdating}
                  title="Auto-update from API"
                  style={{padding:"3px 8px",borderRadius:6,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.08)",
                    color:opts.autoUpdating ? T.text3 : color,fontSize:10,fontWeight:600,cursor:opts.autoUpdating?"not-allowed":"pointer",
                    display:"inline-flex",alignItems:"center",gap:4,opacity:opts.autoUpdating?0.6:1}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    style={opts.autoUpdating ? {animation:"spin 1s linear infinite"} : {}}>
                    <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {opts.autoUpdating ? "Checking…" : "Auto-update"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div>
        <PageLoader ready={!finLoading}/>

        {/* Overview Cards */}
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginBottom:28}}>
          {/* Total Active Credits Value */}
          <div style={{flex:"1 1 200px",padding:"20px 22px",borderRadius:14,
            background:`linear-gradient(135deg, ${CREDIT}10, ${CREDIT}04)`,
            border:`1px solid ${CREDIT}25`}}>
            <div style={{color:T.text3,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
              Total Active Credits Value
            </div>
            <div style={{color:CREDIT,fontSize:28,fontWeight:800}}>
              ${totalCreditsUsd.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
            </div>
            <div style={{color:T.text3,fontSize:10,marginTop:4}}>{Math.round(totalCredits).toLocaleString()} credits across all accounts</div>
          </div>

          {/* Studio Coverage */}
          <div style={{flex:"1 1 200px",padding:"20px 22px",borderRadius:14,
            background:hasDeficit ? "rgba(255,45,111,0.06)" : "rgba(0,229,204,0.06)",
            border:`1px solid ${hasDeficit ? T.danger : CREDIT}25`}}>
            <div style={{color:T.text3,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
              Studio Coverage
            </div>
            <div style={{color:hasDeficit ? T.danger : CREDIT,fontSize:28,fontWeight:800}}>
              ${studioUsd.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
            </div>
            <div style={{color:T.text3,fontSize:10,marginTop:4}}>All provider balances combined</div>
          </div>

          {/* Deficit / Surplus */}
          <div style={{flex:"1 1 200px",padding:"20px 22px",borderRadius:14,
            background:hasDeficit ? "rgba(255,45,111,0.08)" : hasSurplus ? "rgba(0,229,204,0.06)" : "rgba(255,255,255,0.02)",
            border:`1px solid ${hasDeficit ? T.danger : hasSurplus ? CREDIT : "rgba(255,255,255,0.08)"}25`}}>
            <div style={{color:T.text3,fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
              {hasDeficit ? "Deficit" : hasSurplus ? "Surplus" : "Balance"}
            </div>
            <div style={{color:hasDeficit ? T.danger : hasSurplus ? CREDIT : T.text2,fontSize:28,fontWeight:800}}>
              {hasDeficit ? "−" : hasSurplus ? "+" : ""}${Math.abs(deficit).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
            </div>
            <div style={{color:T.text3,fontSize:10,marginTop:4}}>
              {hasDeficit ? "Not enough to cover all credits" : hasSurplus ? "Fully covered with surplus" : "Exactly covered"}
            </div>
          </div>
        </div>

        {/* Kling Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{width:28,height:28,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
              <div style={{width:20,height:20,borderRadius:4,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <img src="/kling-logo.png" style={{width:"100%",height:"100%",objectFit:"contain"}} alt="Kling"/>
              </div>
            </div>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>Kling</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>Studio Account</span>
          </div>

          <div style={{color:T.text3,fontSize:10,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",margin:"4px 0 0 44px"}}>Video Resource Pack</div>
          {finRow(
            "Kling Points Balance",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M6 12h12"/></svg>,
            klingPts, Y,
            finEditingKlingPts, setFinEditingKlingPts, finKlingPts, setFinKlingPts, "klingPoints",
            { decimals: 1 }
          )}
          {finRow(
            "Per-Point Value (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            klingPPV, Y,
            finEditingKlingPPV, setFinEditingKlingPPV, finKlingPPV, setFinKlingPPV, "klingPointValue"
          )}
          <div style={{padding:"6px 0 12px 44px",color:T.text3,fontSize:11}}>
            Coverage: <span style={{color:Y,fontWeight:700}}>${klingUsd.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
            <span style={{opacity:0.5}}> ({klingPts.toLocaleString()} pts × ${klingPPV})</span>
          </div>

          <div style={{color:T.text3,fontSize:10,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",margin:"4px 0 0 44px"}}>Image Resource Pack</div>
          {finRow(
            "Kling Image Points Balance",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
            klingImgPts, Y,
            finEditingKlingImgPts, setFinEditingKlingImgPts, finKlingImgPts, setFinKlingImgPts, "klingImagePoints",
            { decimals: 1 }
          )}
          {finRow(
            "Per-Point Value (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            klingImgPPV, Y,
            finEditingKlingImgPPV, setFinEditingKlingImgPPV, finKlingImgPPV, setFinKlingImgPPV, "klingImagePointValue",
            { decimals: 4 }
          )}
          <div style={{padding:"6px 0 0 44px",color:T.text3,fontSize:11}}>
            Coverage: <span style={{color:Y,fontWeight:700}}>${klingImgUsd.toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
            <span style={{opacity:0.5}}> ({klingImgPts.toLocaleString()} pts × ${klingImgPPV})</span>
          </div>
        </div>

        {/* Grok Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{width:28,height:28,borderRadius:6,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
              <div style={{width:20,height:20,borderRadius:4,background:"#000",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5"><path d="M4 4l16 16M20 4L4 20"/></svg>
              </div>
            </div>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>Grok</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>Studio Account</span>
          </div>

          {finRow(
            "Grok Balance (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            grokBal, CREDIT,
            finEditingGrokBal, setFinEditingGrokBal, finGrokBal, setFinGrokBal, "grokBalance",
            { autoUpdate: handleGrokAutoUpdate, autoUpdating: finGrokChecking }
          )}
        </div>

        {/* Gemini Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <GeminiLogoShared s={28}/>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>Gemini</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>Gemini API · prepaid in AI Studio</span>
          </div>

          {/* Credits status: the Gemini API has no balance endpoint, so the
              only ground truth is whether calls succeed or 402. Every Gemini
              job updates it, and so does the Check status button. */}
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",padding:"10px 0 4px 44px"}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700,
              color: googleStatus === "active" ? T.success : googleStatus === "depleted" ? T.danger : T.text3,
              background: googleStatus === "active" ? `${T.success}18` : googleStatus === "depleted" ? `${T.danger}18` : "rgba(255,255,255,0.06)"}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:"currentColor"}}/>
              {googleStatus === "active" ? "Credits active" : googleStatus === "depleted" ? "No credits - Gemini requests failing" : "Status unknown"}
            </span>
            <span style={{color:T.text3,fontSize:10}}>checked {fmtWhen(d.googleBillingCheckedAt)}</span>
            <button onClick={handleGoogleStatusCheck} disabled={finGoogleStatusChecking}
              style={{padding:"3px 8px",borderRadius:6,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.08)",
                color:finGoogleStatusChecking ? T.text3 : CREDIT,fontSize:10,fontWeight:600,cursor:finGoogleStatusChecking?"wait":"pointer",
                opacity:finGoogleStatusChecking?0.6:1}}>
              {finGoogleStatusChecking ? "Checking…" : "Check status"}
            </button>
          </div>

          {finRow(
            "Gemini Balance (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            googleBal, CREDIT,
            finEditingGoogleBal, setFinEditingGoogleBal, finGoogleBal, setFinGoogleBal, "googleBalance",
          )}
          <div style={{padding:"6px 0 0 44px",color:T.text3,fontSize:11,lineHeight:1.5}}>
            Estimate: set it to the balance on{" "}
            <a href="https://aistudio.google.com/billing" target="_blank" rel="noopener noreferrer" style={{color:CREDIT}}>AI Studio Billing</a>
            {" "}after each purchase, and each Gemini job deducts its cost.
            <span style={{opacity:0.7}}> Last set {fmtWhen(d.googleBalanceSetAt)}.</span>
          </div>

          {/* Auto-reload mirror: Google tops the prepaid balance up on its own
              when auto-reload is on in AI Studio. Copying the same settings
              here lets the ledger apply the same top-ups between syncs. */}
          <div style={{marginTop:10,padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
              <div style={{minWidth:0,flex:"1 1 220px"}}>
                <div style={{color:T.text2,fontSize:12,fontWeight:600,marginBottom:2}}>Auto-reload</div>
                <div style={{color:T.text3,fontSize:10}}>Turn it on in AI Studio Billing, then copy the same settings here so the balance above tracks Google's top-ups.</div>
              </div>
              {!finArEditing && (
                <button onClick={() => {
                  setFinAr({
                    enabled: googleAr.enabled,
                    threshold: googleAr.threshold ? String(googleAr.threshold) : "",
                    amount: googleAr.amount ? String(googleAr.amount) : "",
                    monthlyCap: googleAr.monthlyCap ? String(googleAr.monthlyCap) : "",
                  });
                  setFinArEditing(true);
                }} style={{background:"rgba(255,255,255,0.06)",color:T.text2,border:"1px solid rgba(255,255,255,0.08)",
                  padding:"6px 14px",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>Edit</button>
              )}
            </div>
            {finArEditing ? (
              <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.06)",display:"flex",flexWrap:"wrap",alignItems:"flex-end",gap:12}}>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  <span style={{fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3}}>On</span>
                  <button type="button" onClick={() => setFinAr(a => ({ ...a, enabled: !a.enabled }))}
                    title={finAr.enabled ? "On - click to turn off" : "Off - click to turn on"}
                    style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background: finAr.enabled ? T.success : "rgba(255,255,255,0.08)",position:"relative",transition:"background 0.15s"}}>
                    <span style={{position:"absolute",top:2,left: finAr.enabled ? 18 : 2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
                  </button>
                </div>
                {[["threshold", "When below ($)"], ["amount", "Reload ($)"], ["monthlyCap", "Monthly cap ($)"]].map(([k, label]) => (
                  <label key={k} style={{display:"flex",flexDirection:"column",gap:4}}>
                    <span style={{fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3}}>{label}</span>
                    <input type="number" step="any" min="0" value={finAr[k]} placeholder={k === "monthlyCap" ? "none" : "0"}
                      onChange={e => setFinAr(a => ({ ...a, [k]: e.target.value }))}
                      style={{padding:"6px 10px",borderRadius:8,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",
                        color:T.text,fontSize:13,fontWeight:700,width:110,outline:"none",fontFamily:"inherit"}}/>
                  </label>
                ))}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={handleGoogleAutoReloadSave} disabled={finSaving}
                    style={{padding:"6px 12px",borderRadius:8,background:CREDIT,border:"none",color:T.dark,fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    Save
                  </button>
                  <button onClick={() => setFinArEditing(false)}
                    style={{padding:"6px 10px",borderRadius:8,background:"rgba(255,255,255,0.06)",border:"none",color:T.text3,fontSize:11,cursor:"pointer"}}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div style={{marginTop:8,fontSize:11,color:T.text2}}>
                {googleAr.enabled ? (
                  <>
                    When below <b>${googleAr.threshold}</b>, add <b>${googleAr.amount}</b>
                    {googleAr.monthlyCap ? <> · cap ${googleAr.monthlyCap}/month</> : null}
                    <span style={{color:T.text3}}>
                      {" "}· ${(d.googleAutoReloadedThisMonth || 0).toFixed(2)} reloaded this month
                      {d.googleLastAutoReloadAt ? `, last ${fmtWhen(d.googleLastAutoReloadAt)}` : ""}
                    </span>
                  </>
                ) : (
                  <span style={{color:T.text3}}>Off: the balance only goes down until you set it again.</span>
                )}
              </div>
            )}
          </div>

          {/* Spend verification: the Gemini API doesn't return per-call
              cost, so we compare our own running total to GCP's actual
              billing after the fact. The button sums the cost field on
              every Gemini job in the last 30 days; admin compares that
              to the Generative Language API line in GCP Console -> Billing. */}
          <div style={{marginTop:10,padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
              <div>
                <div style={{color:T.text2,fontSize:12,fontWeight:600,marginBottom:2}}>Estimated 30-day Gemini spend</div>
                <div style={{color:T.text3,fontSize:10}}>The Gemini API doesn't return per-call cost. This sum is what we charged users; compare to actual GCP billing.</div>
              </div>
              <button onClick={handleGoogleSpendCheck} disabled={finGoogleChecking} style={{
                background:finGoogleChecking ? T.surface2 : Y, color:T.dark, border:"none",
                padding:"6px 14px", borderRadius:6, fontSize:11, fontWeight:700,
                cursor:finGoogleChecking ? "wait" : "pointer", flexShrink:0,
              }}>{finGoogleChecking ? "Checking…" : "Check spend"}</button>
            </div>
            {finGoogleSpend && (
              <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.06)",fontSize:11,color:T.text2,display:"flex",flexDirection:"column",gap:4}}>
                <div>Total cost: <span style={{color:T.text,fontWeight:700}}>${finGoogleSpend.totalCostUsd.toFixed(4)}</span> · {finGoogleSpend.jobsWithCost} of {finGoogleSpend.jobs} jobs had a cost</div>
                {Object.entries(finGoogleSpend.byModel || {}).map(([m, v]) => (
                  <div key={m} style={{color:T.text3,fontSize:10,paddingLeft:10}}>{m}: ${v.toFixed(4)}</div>
                ))}
                <div style={{color:T.text3,fontSize:10,marginTop:4}}>Window: last {finGoogleSpend.windowDays} day{finGoogleSpend.windowDays===1?'':'s'} (since {new Date(finGoogleSpend.sinceIso).toLocaleDateString()})</div>
              </div>
            )}
          </div>
        </div>

        {/* OpenAI Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <ModelIcon model="openai-image-2.5-flare" s={28}/>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>OpenAI</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>GPT Image · prepaid credits</span>
          </div>

          {finRow(
            "OpenAI Balance (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            openaiBal, CREDIT,
            finEditingOpenaiBal, setFinEditingOpenaiBal, finOpenaiBal, setFinOpenaiBal, "openaiBalance",
          )}
          <div style={{padding:"6px 0 0 44px",color:T.text3,fontSize:11,lineHeight:1.5}}>
            Estimate: set it to the credit balance on{" "}
            <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener noreferrer" style={{color:CREDIT}}>OpenAI Billing</a>
            {" "}after each purchase, and each GPT Image job deducts its exact token cost.
          </div>

          {/* Actual spend from OpenAI's Costs API (needs the admin key). */}
          <div style={{marginTop:10,padding:"10px 14px",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
              <div style={{minWidth:0,flex:"1 1 220px"}}>
                <div style={{color:T.text2,fontSize:12,fontWeight:600,marginBottom:2}}>Actual 30-day OpenAI spend</div>
                <div style={{color:T.text3,fontSize:10}}>
                  {d.openaiSpend
                    ? <>${Number(d.openaiSpend.totalUsd || 0).toFixed(2)} over {d.openaiSpend.days} days · checked {d.openaiSpend.checkedAt ? new Date(d.openaiSpend.checkedAt).toLocaleString() : "never"}</>
                    : "Read from OpenAI's Costs API. Needs the OpenAI admin key in Studio API Keys."}
                </div>
              </div>
              <button onClick={handleOpenAISpendCheck} disabled={finOpenaiChecking} style={{
                background:finOpenaiChecking ? T.surface2 : Y, color:T.dark, border:"none",
                padding:"6px 14px", borderRadius:6, fontSize:11, fontWeight:700,
                cursor:finOpenaiChecking ? "wait" : "pointer", flexShrink:0,
              }}>{finOpenaiChecking ? "Checking…" : "Check spend"}</button>
            </div>
          </div>
        </div>

        {/* Recraft Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <ModelIcon model="recraft-v4-vector" s={28}/>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>Recraft</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>Vector models · prepaid API units</span>
          </div>

          {finRow(
            "Recraft Balance (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            recraftBal, CREDIT,
            finEditingRecraftBal, setFinEditingRecraftBal, finRecraftBal, setFinRecraftBal, "recraftBalance",
            { autoUpdate: handleRecraftAutoUpdate, autoUpdating: finRecraftChecking }
          )}
          <div style={{padding:"6px 0 0 44px",color:T.text3,fontSize:11,lineHeight:1.5}}>
            Each Recraft job deducts its cost; Auto-update reads the live balance from Recraft (1,000 API units = $1).
          </div>
        </div>

        {/* BFL FLUX Section */}
        <div style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <ModelIcon model="bfl-flux-2-pro" s={28}/>
            <span style={{color:T.text2,fontSize:18,fontWeight:800}}>BFL FLUX</span>
            <span style={{color:T.text3,fontSize:11,marginLeft:4}}>FLUX.2 models · prepaid credits</span>
          </div>

          {finRow(
            "BFL Balance (USD)",
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            bflBal, CREDIT,
            finEditingBflBal, setFinEditingBflBal, finBflBal, setFinBflBal, "bflBalance",
            { autoUpdate: handleBflAutoUpdate, autoUpdating: finBflChecking }
          )}
          <div style={{padding:"6px 0 0 44px",color:T.text3,fontSize:11,lineHeight:1.5}}>
            Each FLUX job deducts its cost; Auto-update reads the live balance from BFL (1 credit = $0.01).
          </div>
        </div>

        {/* Platform Links — quick access to each provider's console,
            API docs, and where to top up credits / billing. */}
        <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>Platform Links</div>
          <div style={{color:T.text3,fontSize:11,lineHeight:1.5,marginBottom:14}}>
            Provider consoles, API documentation, and where to add funds / resource packs.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(min(260px, 100%), 1fr))",gap:10}}>
            {[
              { name:'Kling',   model:'kling-2.1-pro',
                links:[
                  ['Console',  'https://app.klingai.com/global/dev/'],
                  ['API Docs', 'https://app.klingai.com/global/dev/document-api/quickStart/productIntroduction/overview'],
                  ['Buy Credits', 'https://app.klingai.com/global/dev/'],
                ]},
              { name:'Grok (xAI)', model:'grok-imagine-720p',
                links:[
                  ['Console',  'https://console.x.ai/'],
                  ['API Docs', 'https://docs.x.ai/'],
                  ['Billing',  'https://console.x.ai/team/default/billing'],
                ]},
              { name:'Gemini (Google)', model:'google-nano-banana-pro',
                links:[
                  ['AI Studio', 'https://aistudio.google.com/apikey'],
                  ['API Docs',  'https://ai.google.dev/gemini-api/docs'],
                  ['Billing',   'https://aistudio.google.com/billing'],
                  ['GCP Billing','https://console.cloud.google.com/billing'],
                ]},
              { name:'Recraft', model:'recraft-v4-vector',
                links:[
                  ['Console',  'https://www.recraft.ai/'],
                  ['API Docs', 'https://www.recraft.ai/docs'],
                  ['Buy Units','https://www.recraft.ai/profile/api'],
                ]},
              { name:'OpenAI', model:'openai-image-2.5-flare',
                links:[
                  ['Platform', 'https://platform.openai.com/'],
                  ['API Docs', 'https://developers.openai.com/api/docs/guides/image-generation'],
                  ['Billing',  'https://platform.openai.com/settings/organization/billing/overview'],
                ]},
              { name:'BFL FLUX', model:'bfl-flux-2-pro',
                links:[
                  ['Dashboard','https://dashboard.bfl.ai/'],
                  ['API Docs', 'https://docs.bfl.ai/'],
                  ['Top Up',   'https://dashboard.bfl.ai/'],
                ]},
            ].map(p => (
              <div key={p.name} style={{padding:"12px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.04)",display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:26,height:26,borderRadius:5,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                    <ModelIcon model={p.model} s={20}/>
                  </div>
                  <span style={{color:T.text2,fontSize:14,fontWeight:700}}>{p.name}</span>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {p.links.map(([label, href]) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                      style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:999,border:"1px solid rgba(255,255,255,0.14)",background:"transparent",color:T.text2,fontSize:11,fontWeight:600,textDecoration:"none",transition:"all 0.12s"}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=Y;e.currentTarget.style.color=Y;}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.14)";e.currentTarget.style.color=T.text2;}}>
                      {label}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        {d.updatedAt && (
          <div style={{color:T.text3,fontSize:10,opacity:0.6,marginTop:8}}>
            Last updated: {new Date(d.updatedAt).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  // ─── Gift Credits Dialog (two-step: enter amount -> confirm) ────────────────
  const renderGiftDialog = () => {
    if (!giftDialog) return null;
    const parsedAmt = parseFloat(giftAmount);
    const validAmt = parsedAmt > 0;
    return (
      <div style={{
        position:"fixed",top:0,left:0,right:0,bottom:0,
        background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)",
        display:"flex",alignItems:"center",justifyContent:"center",
        zIndex:9999,
      }} onClick={() => { setGiftDialog(null); setGiftConfirming(false); }}>
        <div onClick={e => e.stopPropagation()} style={{
          background:T.surface,border:`1px solid ${CREDIT}30`,
          borderRadius:16,padding:28,width:"100%",maxWidth:400,
          boxShadow:"0 20px 60px rgba(0,0,0,0.4)",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill={CREDIT} style={{flexShrink:0}}>
              <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/>
            </svg>
            <h3 style={{color:T.text,fontSize:16,fontWeight:700,margin:0}}>Gift Credits</h3>
          </div>

          {!giftConfirming ? (
            /* ── Step 1: Enter amount ── */
            <>
              <p style={{color:T.text3,fontSize:12,margin:"4px 0 20px"}}>
                Gift credits to <span style={{color:T.text2,fontWeight:600}}>{giftDialog.email}</span>
                <br/><span style={{color:T.text3,fontSize:10,opacity:0.7}}>Gifted credits never expire</span>
              </p>
              <label style={{color:T.text2,fontSize:12,fontWeight:600,display:"block",marginBottom:8}}>
                Amount (in credits)
              </label>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <CreditIcon size={16} color={CREDIT}/>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={giftAmount}
                  onChange={e => setGiftAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  onKeyDown={e => { if (e.key === "Enter" && validAmt) setGiftConfirming(true); }}
                  style={{
                    flex:1,padding:"12px 14px",borderRadius:10,
                    background:"rgba(255,255,255,0.04)",border:`1px solid ${CREDIT}40`,
                    color:T.text,fontSize:18,fontWeight:700,outline:"none",fontFamily:"inherit",
                  }}
                />
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:24}}>
                <button onClick={() => { setGiftDialog(null); setGiftConfirming(false); }} style={{
                  padding:"10px 20px",borderRadius:10,
                  background:"rgba(255,255,255,0.06)",border:"none",
                  color:T.text2,fontSize:12,fontWeight:600,cursor:"pointer",
                }}>Cancel</button>
                <button onClick={() => setGiftConfirming(true)} disabled={!validAmt} style={{
                  padding:"10px 24px",borderRadius:10,
                  background:CREDIT,border:"none",
                  color:T.dark,fontSize:12,fontWeight:800,
                  cursor:!validAmt ? "not-allowed" : "pointer",
                  opacity:!validAmt ? 0.5 : 1,
                }}>Next</button>
              </div>
            </>
          ) : (
            /* ── Step 2: Confirm ── */
            <>
              <p style={{color:T.text3,fontSize:12,margin:"4px 0 20px"}}>
                Please confirm this gift:
              </p>
              <div style={{background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"16px 20px",border:`1px solid ${CREDIT}20`,marginBottom:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{color:T.text3,fontSize:11}}>Recipient</span>
                  <span style={{color:T.text,fontSize:12,fontWeight:600}}>{giftDialog.email}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{color:T.text3,fontSize:11}}>Amount</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:3,color:CREDIT,fontSize:18,fontWeight:800}}><CreditIcon size={16} color={CREDIT}/>{Math.round(parsedAmt).toLocaleString()}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={() => setGiftConfirming(false)} style={{
                  padding:"10px 20px",borderRadius:10,
                  background:"rgba(255,255,255,0.06)",border:"none",
                  color:T.text2,fontSize:12,fontWeight:600,cursor:"pointer",
                }}>Back</button>
                <button onClick={handleGiftCredits} disabled={gifting} style={{
                  padding:"10px 24px",borderRadius:10,
                  background:CREDIT,border:"none",
                  color:T.dark,fontSize:12,fontWeight:800,
                  cursor:gifting ? "not-allowed" : "pointer",
                  opacity:gifting ? 0.5 : 1,
                  display:"flex",alignItems:"center",gap:6,
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6"/><polyline points="12 3 12 15"/><path d="M8 7l4-4 4 4"/></svg>
                  {gifting ? "Gifting…" : "Confirm Gift"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── Thumbnail backfill ─────────────────────────────────────────────────────
  const loadThumbBackfill = useCallback(async () => {
    try {
      const s = await api.getThumbnailBackfillStatus();
      setThumbBackfill(s);
    } catch (e) { /* ignore - poll will retry */ }
  }, []);

  // Poll while running; one-shot fetch otherwise. Clears the interval as
  // soon as the worker reports done so we don't spam the endpoint.
  useEffect(() => {
    if (tab !== 'appconfig') return;
    loadThumbBackfill();
    const id = setInterval(() => {
      if (thumbBackfill && thumbBackfill.running) loadThumbBackfill();
    }, 2000);
    return () => clearInterval(id);
  }, [tab, thumbBackfill && thumbBackfill.running, loadThumbBackfill]); // eslint-disable-line

  const handleLoadGrokAudit = async () => {
    setGrokAuditLoading(true);
    setGrokAuditError(null);
    try {
      const r = await api.getGrokCostAudit(100);
      setGrokAudit(r);
    } catch (e) { setGrokAuditError(e.message); }
    setGrokAuditLoading(false);
  };

  const handleStartThumbBackfill = async (opts) => {
    setThumbBackfillKicking(true);
    try {
      const r = await api.startThumbnailBackfill(opts);
      if (r.alreadyRunning) notify('info', 'Backfill is already running.');
      else notify('success', `Thumbnail backfill started${opts && opts.force ? ' (force mode)' : ''}.`);
      // Kick the poll loop alive immediately.
      setTimeout(loadThumbBackfill, 300);
    } catch (e) { notify('error', e.message); }
    setThumbBackfillKicking(false);
  };

  // ─── App Config ──────────────────────────────────────────────────────────────
  const handleCfgSave = async () => {
    setCfgSaving(true);
    try {
      await api.saveAppConfig({
        maxConcurrentJobs: Number(cfgMaxJobs) || 2,
        studioKlingConcurrentMax: Number(cfgStudioMax) || 20,
        studioGrokRpmMax: Number(cfgStudioGrokRpm) || 60,
        imageMaxConcurrentJobs: Number(cfgImgMaxJobs) || 2,
        studioKlingImageConcurrentMax: Number(cfgImgStudioMax) || 20,
        studioGrokImageRpmMax: Number(cfgImgStudioGrokRpm) || 60,
        videoModelDisabled:    Array.from(cfgVideoDisabled),
        videoModelRecommended: Array.from(cfgVideoRecommended),
        imageModelDisabled:    Array.from(cfgImageDisabled),
        imageModelRecommended: Array.from(cfgImageRecommended),
      });
      notify("success", "App config saved");
      loadAppConfig();
    } catch (e) { notify("error", e.message); }
    setCfgSaving(false);
  };

  // Set-mutator helper for the Model Control toggles. Returns a new Set
  // (immutable update) so React picks up the change.
  const toggleSet = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const renderAppConfig = () => {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:24}}>
        <PageLoader ready={!cfgLoading && !skLoading}/>
        <div style={{fontSize:16,fontWeight:800,color:T.title,marginBottom:4}}>App Config</div>

        <div style={{background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"16px 20px",border:"1px solid rgba(255,255,255,0.06)",display:"flex",flexDirection:"column",gap:16}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Video Generation Limits</div>

          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{color:T.text2,fontSize:12,minWidth:240}}>Max concurrent video jobs per user</span>
            <input
              type="number" min="1" max="20" step="1"
              value={cfgMaxJobs}
              onChange={e => setCfgMaxJobs(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{minWidth:240}}>
              <div style={{color:T.text2,fontSize:12}}>Studio Kling video concurrent cap</div>
              <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5,maxWidth:340}}>
                Max in-flight video jobs against the studio Kling key for this app.
                Studio keys are shared across apps - set this to leave headroom
                for the others. Jobs over the cap are queued and submitted as
                slots free up.
              </div>
            </div>
            <input
              type="number" min="1" max="200" step="1"
              value={cfgStudioMax}
              onChange={e => setCfgStudioMax(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{minWidth:240}}>
              <div style={{color:T.text2,fontSize:12}}>Studio Grok video rate cap (per minute)</div>
              <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5,maxWidth:340}}>
                Max studio-key Grok video submissions per rolling 60s window.
                xAI's API limit is 60 RPM total - split this with sibling apps.
                Jobs over the cap are queued and released as the window slides.
              </div>
            </div>
            <input
              type="number" min="1" max="600" step="1"
              value={cfgStudioGrokRpm}
              onChange={e => setCfgStudioGrokRpm(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{height:1,background:"rgba(255,255,255,0.06)",margin:"4px 0"}}/>

          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Image Generation Limits</div>

          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{color:T.text2,fontSize:12,minWidth:240}}>Max concurrent image jobs per user</span>
            <input
              type="number" min="1" max="20" step="1"
              value={cfgImgMaxJobs}
              onChange={e => setCfgImgMaxJobs(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{minWidth:240}}>
              <div style={{color:T.text2,fontSize:12}}>Studio Kling image concurrent cap</div>
              <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5,maxWidth:340}}>
                Max in-flight image-generation jobs against the studio Kling
                key for this app. Tracked separately from the video cap so a
                flood of one type doesn't starve the other.
              </div>
            </div>
            <input
              type="number" min="1" max="200" step="1"
              value={cfgImgStudioMax}
              onChange={e => setCfgImgStudioMax(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
            <div style={{minWidth:240}}>
              <div style={{color:T.text2,fontSize:12}}>Studio Grok image rate cap (per minute)</div>
              <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5,maxWidth:340}}>
                Max studio-key Grok image-generation submissions per rolling
                60s window. Independent bucket from the Grok video cap.
              </div>
            </div>
            <input
              type="number" min="1" max="600" step="1"
              value={cfgImgStudioGrokRpm}
              onChange={e => setCfgImgStudioGrokRpm(e.target.value)}
              style={{...inp,width:70,textAlign:"center",fontSize:13,fontWeight:700}}
            />
          </div>

          <div style={{height:1,background:"rgba(255,255,255,0.06)",margin:"4px 0"}}/>

          <div style={{fontSize:13,fontWeight:700,color:T.text}}>Model Control</div>
          <div style={{color:T.text3,fontSize:11,lineHeight:1.5,maxWidth:600,marginTop:-8}}>
            Toggle model availability and pin which ones show under
            "Recommended" at the top of the model picker. Disabled models
            don't appear in the picker at all. Empty Recommended set falls
            back to the frontend defaults.
          </div>

          {[
            { kind:'video', label:'Video Models', registry: VIDEO_MODEL_REGISTRY, disabled: cfgVideoDisabled, setDisabled: setCfgVideoDisabled, recommended: cfgVideoRecommended, setRecommended: setCfgVideoRecommended },
            { kind:'image', label:'Image Models', registry: IMAGE_MODEL_REGISTRY, disabled: cfgImageDisabled, setDisabled: setCfgImageDisabled, recommended: cfgImageRecommended, setRecommended: setCfgImageRecommended },
          ].map(group => (
            <div key={group.kind} style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3}}>{group.label}</span>
                <span style={{color:T.text3,fontSize:11}}>{group.registry.length}</span>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.06)"}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(min(220px, 100%), 1fr))",gap:8}}>
                {group.registry.map((m) => {
                  const isDisabled    = group.disabled.has(m.id);
                  const isRecommended = group.recommended.has(m.id);
                  return (
                    <div key={m.id} style={{display:"flex",flexDirection:"column",gap:10,padding:"10px 12px",borderRadius:10,border:`1px solid ${isDisabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.12)"}`,background: isDisabled ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",transition:"all 0.15s"}}>
                      <div style={{minWidth:0}}>
                        <div style={{color: isDisabled ? T.text3 : T.text, fontSize:13, fontWeight:700, opacity: isDisabled ? 0.5 : 1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{m.label}</div>
                        <div style={{color:T.text3,fontSize:10,fontFamily:"monospace",opacity:0.7,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2}}>{m.id}</div>
                      </div>
                      <div style={{display:"flex",gap:14,alignItems:"flex-end"}}>
                        <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start"}}>
                          <span style={{fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3}}>Available</span>
                          <button onClick={() => group.setDisabled(s => toggleSet(s, m.id))}
                            title={isDisabled ? "Disabled - click to enable" : "Enabled - click to disable"}
                            style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background: isDisabled ? "rgba(255,255,255,0.08)" : T.success, position:"relative",transition:"background 0.15s"}}>
                            <span style={{position:"absolute",top:2,left: isDisabled ? 2 : 18,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
                          </button>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-start"}}>
                          <span style={{fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3}}>Recommended</span>
                          <button onClick={() => group.setRecommended(s => toggleSet(s, m.id))}
                            disabled={isDisabled}
                            title={isDisabled ? "Enable the model first" : (isRecommended ? "Pinned to Recommended - click to unpin" : "Add to Recommended")}
                            style={{width:38,height:22,borderRadius:11,border:"none",cursor:isDisabled?"not-allowed":"pointer",background: isRecommended && !isDisabled ? Y : "rgba(255,255,255,0.08)", position:"relative",opacity:isDisabled?0.4:1,transition:"background 0.15s"}}>
                            <span style={{position:"absolute",top:2,left: isRecommended ? 18 : 2,width:18,height:18,borderRadius:"50%",background: isRecommended && !isDisabled ? T.dark : "#fff",transition:"left 0.15s"}}/>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            onClick={handleCfgSave}
            disabled={cfgSaving}
            style={{alignSelf:"flex-start",padding:"8px 20px",borderRadius:10,border:"none",background:Y,color:T.dark,fontWeight:700,fontSize:12,cursor:cfgSaving?"wait":"pointer",opacity:cfgSaving?0.6:1,fontFamily:"Mulish,sans-serif"}}
          >
            {cfgSaving ? "Saving…" : "Save"}
          </button>
        </div>

        {/* Studio API Keys section */}
        <div style={{background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"20px 24px",border:"1px solid rgba(255,255,255,0.06)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text}}>Studio API Keys</div>
            <button onClick={handleCopyMyKeys} disabled={skSaving}
              style={{padding:"8px 16px",borderRadius:10,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:T.text2,fontSize:11,fontWeight:600,cursor:skSaving?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6,opacity:skSaving?0.6:1}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              Copy My Keys
            </button>
          </div>
          {renderStudioKeys()}
        </div>

        {/* Maintenance section — backfills + one-off jobs */}
        <div style={{background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"20px 24px",border:"1px solid rgba(255,255,255,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:16}}>Maintenance</div>

          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:"1 1 240px",minWidth:240}}>
                <div style={{color:T.text2,fontSize:12,fontWeight:600}}>Image thumbnails</div>
                <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5}}>
                  Walks both buckets and generates a 320px JPEG thumbnail for any image without one.
                  Resumable - skips images already done. Safe to re-run.
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                <button onClick={()=>handleStartThumbBackfill()}
                  disabled={thumbBackfillKicking || (thumbBackfill && thumbBackfill.running)}
                  style={{padding:"8px 16px",borderRadius:10,background: (thumbBackfill && thumbBackfill.running) ? "rgba(255,255,255,0.06)" : Y, border:"1px solid rgba(255,255,255,0.1)", color: (thumbBackfill && thumbBackfill.running) ? T.text2 : T.dark, fontSize:11, fontWeight:700, cursor: (thumbBackfillKicking || (thumbBackfill && thumbBackfill.running)) ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:6}}>
                  {(thumbBackfill && thumbBackfill.running) ? "Running…" : (thumbBackfillKicking ? "Starting…" : "Backfill thumbnails")}
                </button>
                <button onClick={()=>handleStartThumbBackfill({ force: true })}
                  disabled={thumbBackfillKicking || (thumbBackfill && thumbBackfill.running)}
                  title="Regenerate every thumbnail, ignoring the existence check. Use after a thumb-path scheme change or to recover from a wrong-content thumb."
                  style={{padding:"8px 14px",borderRadius:10,background:"rgba(255,255,255,0.04)",border:`1px solid ${T.danger}55`,color:T.danger,fontSize:11,fontWeight:600,cursor: (thumbBackfillKicking || (thumbBackfill && thumbBackfill.running)) ? "not-allowed" : "pointer",display:"flex",alignItems:"center",gap:6}}>
                  Force rebuild
                </button>
              </div>
            </div>

            {thumbBackfill && (thumbBackfill.running || thumbBackfill.processed > 0 || thumbBackfill.startedAt) && (
              <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.06)",display:"flex",flexDirection:"column",gap:6}}>
                {thumbBackfill.total > 0 && (
                  <div style={{height:6,borderRadius:3,background:"rgba(255,255,255,0.06)",overflow:"hidden"}}>
                    <div style={{width:`${Math.min(100, Math.round((thumbBackfill.processed / Math.max(1, thumbBackfill.total)) * 100))}%`, height:"100%", background:Y, transition:"width 0.4s"}}/>
                  </div>
                )}
                <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:11,color:T.text3}}>
                  <span><b style={{color:T.text2}}>{thumbBackfill.processed || 0}</b> / {thumbBackfill.total || "?"} processed</span>
                  <span><b style={{color:T.text2}}>{thumbBackfill.generated || 0}</b> generated</span>
                  <span><b style={{color:T.text2}}>{thumbBackfill.skipped || 0}</b> skipped</span>
                  {(thumbBackfill.errors || 0) > 0 && (
                    <span style={{color:T.danger}}><b>{thumbBackfill.errors}</b> errors</span>
                  )}
                  {thumbBackfill.bucket && <span style={{opacity:0.7}}>bucket: {thumbBackfill.bucket}</span>}
                </div>
                {thumbBackfill.lastError && (
                  <div style={{fontSize:10,color:T.danger,opacity:0.8}}>last error: {thumbBackfill.lastError}</div>
                )}
                {thumbBackfill.stale && (
                  <div style={{fontSize:10,color:T.danger,opacity:0.8}}>worker appears stale - re-run to resume.</div>
                )}
                {thumbBackfill.finishedAt && !thumbBackfill.running && !thumbBackfill.stale && (
                  <div style={{fontSize:10,color:T.text3}}>finished {new Date(thumbBackfill.finishedAt).toLocaleString()}</div>
                )}
              </div>
            )}

            <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:"1 1 240px",minWidth:240}}>
                <div style={{color:T.text2,fontSize:12,fontWeight:600}}>Grok cost audit</div>
                <div style={{color:T.text3,fontSize:10,marginTop:2,lineHeight:1.5}}>
                  Lists recent Grok jobs with our pre-submit estimate vs the upstream-reported actual cost. Use to spot drift in the pricing tables.
                </div>
              </div>
              <button onClick={handleLoadGrokAudit} disabled={grokAuditLoading}
                style={{padding:"8px 16px",borderRadius:10,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:T.text2,fontSize:11,fontWeight:600,cursor:grokAuditLoading?"wait":"pointer",display:"flex",alignItems:"center",gap:6}}>
                {grokAuditLoading ? "Loading…" : "Load Grok audit"}
              </button>
            </div>

            {grokAuditError && (
              <div style={{padding:"8px 10px",borderRadius:6,border:`1px solid ${T.danger}55`,background:`${T.danger}15`,color:T.danger,fontSize:11}}>{grokAuditError}</div>
            )}

            {grokAudit && grokAudit.rows && (
              <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.06)",overflowX:"auto"}}>
                <div style={{color:T.text3,fontSize:11,marginBottom:6}}>
                  {grokAudit.count} jobs. Drift = actual - estimate (credits). Click a row's ID to open.
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead>
                    <tr style={{borderBottom:"1px solid rgba(255,255,255,0.08)",color:T.text3,textTransform:"uppercase",fontSize:9,letterSpacing:0.5}}>
                      <th style={{padding:"6px 8px",textAlign:"left"}}>Type</th>
                      <th style={{padding:"6px 8px",textAlign:"left"}}>Model</th>
                      <th style={{padding:"6px 8px",textAlign:"right"}}>n / dur</th>
                      <th style={{padding:"6px 8px",textAlign:"left"}}>Res</th>
                      <th style={{padding:"6px 8px",textAlign:"right"}}>Refs</th>
                      <th style={{padding:"6px 8px",textAlign:"right"}}>Est cr</th>
                      <th style={{padding:"6px 8px",textAlign:"right"}}>Actual cr</th>
                      <th style={{padding:"6px 8px",textAlign:"right"}}>Drift</th>
                      <th style={{padding:"6px 8px",textAlign:"left"}}>Job</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grokAudit.rows.map(r => {
                      const drift = r.deltaCredits;
                      const driftColor = drift == null ? T.text3 : drift === 0 ? T.text3 : drift > 0 ? T.danger : "#5BBFB8";
                      return (
                        <tr key={r.id} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                          <td style={{padding:"5px 8px",color:T.text3}}>{r.outputType}</td>
                          <td style={{padding:"5px 8px",color:T.text2,fontFamily:"monospace"}}>{r.model}</td>
                          <td style={{padding:"5px 8px",textAlign:"right",color:T.text2}}>{r.outputType === 'image' ? `n=${r.n}` : `${r.duration}s`}</td>
                          <td style={{padding:"5px 8px",color:T.text3}}>{r.resolution || "—"}</td>
                          <td style={{padding:"5px 8px",textAlign:"right",color:T.text3}}>{r.refsCount}</td>
                          <td style={{padding:"5px 8px",textAlign:"right",color:T.text3,fontFamily:"monospace"}}>{r.estimatedCredits.toLocaleString()}</td>
                          <td style={{padding:"5px 8px",textAlign:"right",color:T.text2,fontFamily:"monospace"}}>{r.actualCredits != null ? r.actualCredits.toLocaleString() : "—"}</td>
                          <td style={{padding:"5px 8px",textAlign:"right",color:driftColor,fontFamily:"monospace",fontWeight:drift && drift !== 0 ? 700 : 400}}>{drift == null ? "—" : (drift > 0 ? `+${drift}` : drift)}</td>
                          <td style={{padding:"5px 8px"}}>
                            <button onClick={()=>navigate(`/admin/job-queue/${r.id}`)}
                              style={{background:"none",border:"none",color:T.text3,cursor:"pointer",fontFamily:"monospace",fontSize:10,padding:0,textDecoration:"underline"}}>
                              {r.id.slice(0, 8)}…
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Job Queue ──────────────────────────────────────────────────────────────
  const loadJobQueue = useCallback(() => {
    api.getJobQueue()
      .then(data => { setQueueData(data); setQueueLoading(false); })
      .catch(e => { console.warn('Job queue load failed:', e.message); setQueueLoading(false); });
  }, []);

  useEffect(() => {
    // Skip the queue refresh poll while the detail view is open - it's a
    // separate page that fetches its own data.
    if (tab !== "jobqueue" || jobDetailId) return;
    setQueueLoading(true);
    loadJobQueue();
    const id = setInterval(loadJobQueue, 5000);
    return () => clearInterval(id);
  }, [tab, jobDetailId, loadJobQueue]);

  const renderJobQueue = () => {
    const ts = (s) => {
      if (!s) return "—";
      const d = new Date(s);
      if (isNaN(d.getTime())) return "—";
      const now = serverNow();
      const diff = Math.floor((now - d.getTime()) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
      return d.toLocaleDateString();
    };
    const statusColor = { queued: T.text3, processing: Y, ready: T.success, failed: T.danger };

    // Live elapsed timer for active rows. Counts from processingStartedAt
    // (so it resets to 0 the moment a job flips queued -> processing) or
    // createdAt for jobs still in the queue. serverNow() compensates for
    // clock skew so the first tick lands at 0 instead of "5s" when the
    // user's clock is ahead of Cloud Run.
    const ElapsedTimer = ({ baselineIso, suffix }) => {
      const [text, setText] = useState("");
      useEffect(() => {
        if (!baselineIso) { setText("—"); return; }
        const update = () => {
          const diff = Math.max(0, Math.floor((serverNow() - new Date(baselineIso).getTime()) / 1000));
          const m = Math.floor(diff / 60);
          const s = diff % 60;
          setText(m > 0 ? `${m}m ${s}s` : `${s}s`);
        };
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
      }, [baselineIso]);
      return <span>{text}{suffix && text !== '—' ? ` ${suffix}` : ''}</span>;
    };

    const meta = queueData?.meta || {};
    const active = queueData?.active || [];
    const history = queueData?.history || [];
    const queuedKling = active.filter(j => j.platform === 'kling' && j.queuePending).length;
    const queuedGrok  = active.filter(j => j.platform === 'grok'  && j.queuePending).length;

    const Stat = ({ label, value, sub }) => (
      <div style={{padding:"10px 14px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,minWidth:140}}>
        <div style={{color:T.text3,fontSize:10,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase"}}>{label}</div>
        <div style={{color:T.title,fontSize:18,fontWeight:800,marginTop:4}}>{value}</div>
        {sub && <div style={{color:T.text3,fontSize:10,marginTop:2}}>{sub}</div>}
      </div>
    );

    // Resolution for the job-queue rows. Image jobs carry the value
    // explicitly on `imageResolution` (`'1k'`/`'2k'`/`'4k'`/`'svg'`);
    // video jobs encode it in the model ID suffix. Image jobs use the
    // `Img*` keys so ResPill picks up the image-context colors (blue/
    // purple/gold) instead of the video-context ones (gold/orange-red).
    const getResolution = (j) => {
      if (!j) return '720p';
      if (j.outputType === 'image' && j.imageResolution) {
        const r = String(j.imageResolution).toLowerCase();
        if (r === '1k') return 'Img1K';
        if (r === '2k') return 'Img2K';
        if (r === '4k') return 'Img4K';
        if (r === 'svg') return 'SVG';
        return r.toUpperCase();
      }
      const id = j.model || '';
      if (id.includes('-4k')) return '4K';
      if (id.includes('-1080p')) return '1080p';
      if (id.includes('-720p')) return '720p';
      if (id.includes('-480p')) return '480p';
      if (id.includes('-pro')) return '1080p';
      return '720p';
    };

    const handleRecover = async (jobId, action) => {
      // check-now is benign (read-only upstream check + finalize-if-ready),
      // so skip the confirm dialog and just run it. The other two actions
      // are state changes worth confirming.
      if (action !== 'check-now') {
        const verb = action === 'mark-failed' ? 'mark this job failed' : 'resume polling on this job';
        const ok = await confirm({
          title: action === 'mark-failed' ? 'Mark job failed?' : 'Resume polling?',
          message: `Are you sure you want to ${verb}?`,
          confirmLabel: action === 'mark-failed' ? 'Mark Failed' : 'Resume',
          danger: action === 'mark-failed',
        });
        if (!ok) return;
      }
      setRecoveringId(jobId);
      try {
        const res = await api.recoverJob(jobId, action);
        if (action === 'check-now') {
          // Server returned the resulting status — surface it so the user
          // knows whether the upstream actually had a finished video ready.
          const s = res.status;
          if (s === 'ready') notify('success', 'Job finished — output ready');
          else if (s === 'failed') notify('error', `Job marked failed: ${res.error || 'upstream failed'}`);
          else notify('success', 'Upstream still processing — try again shortly');
        } else {
          notify('success', res.message || (action === 'mark-failed' ? 'Marked failed' : 'Polling resumed'));
        }
        loadJobQueue();
      } catch (e) {
        notify('error', e.message);
      } finally {
        setRecoveringId(null);
      }
    };

    const Row = ({ j, last }) => {
      const credits = j.cost != null ? Math.round(Number(j.cost) / 0.002) : null;
      const isInFlight = j.status === 'processing' || j.status === 'queued';
      const isRecovering = recoveringId === j.id;
      const noTaskId = !j.klingTaskId && !j.grokRequestId;
      // A failed job can still be recovered if it has an upstream task
      // ID - usually because the original poll loop timed out before
      // Kling finished. Show the recovery buttons there too so admins
      // can do a one-shot check and finalize the video if it's ready.
      const recoverable = isInFlight || (j.status === 'failed' && !noTaskId);
      const goDetail = () => navigate(`/admin/job-queue/${j.id}`);
      return (
        <tr onClick={goDetail}
          style={{borderBottom:last?"none":"1px solid rgba(255,255,255,0.04)",cursor:"pointer",transition:"background 0.12s"}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <td style={{padding:"8px 10px",fontSize:11}}>
            <span style={{color:statusColor[j.status]||T.text3,fontWeight:700,textTransform:"uppercase",fontSize:10}}>
              {j.queuePending ? "queued (waiting)" : j.status}
            </span>
          </td>
          <td style={{padding:"8px 10px",fontSize:11,color:T.text2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:240}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
              <ModelIcon model={j.model} s={14}/>
              <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>
                {j.model || "—"}
                {j.model === 'kling-effects' && j.klingEffectScene && (
                  <span style={{color:T.text3,marginLeft:6,fontFamily:"monospace",fontSize:10}}>· {j.klingEffectScene}</span>
                )}
              </span>
              {j.model && <ResPill res={getResolution(j)} small/>}
            </span>
          </td>
          <td style={{padding:"8px 10px",fontSize:11,color:T.text2,textAlign:"center"}}>{j.duration ? `${j.duration}s` : "—"}</td>
          <td style={{padding:"8px 10px",fontSize:11,color:T.text2,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.email || "—"}</td>
          <td style={{padding:"8px 10px",fontSize:11,color:T.text3,textAlign:"right",whiteSpace:"nowrap"}}>
            {j.status === 'processing'
              ? <span style={{color:"#5BBFB8",fontFamily:"monospace",fontWeight:700}}><ElapsedTimer baselineIso={j.processingStartedAt || j.updatedAt || j.createdAt}/></span>
              : j.status === 'queued'
                ? <span style={{fontFamily:"monospace"}}><ElapsedTimer baselineIso={j.createdAt} suffix="queued"/></span>
                : ts(j.createdAt)}
          </td>
          <td style={{padding:"8px 10px",fontSize:11,color:credits?CREDIT:T.text3,textAlign:"right",fontFamily:"monospace",whiteSpace:"nowrap"}}>
            {credits != null ? (
              <span style={{display:"inline-flex",alignItems:"center",gap:3,justifyContent:"flex-end"}}>
                <CreditIcon size={10} color={CREDIT}/>{credits.toLocaleString()}
              </span>
            ) : "—"}
          </td>
          <td style={{padding:"8px 10px",fontSize:11,color:j.cost?Y:T.text3,textAlign:"right",fontFamily:"monospace"}}>{j.cost!=null ? `$${Number(j.cost).toFixed(2)}` : "—"}</td>
          <td onClick={e=>e.stopPropagation()} style={{padding:"8px 10px",fontSize:11,textAlign:"right",whiteSpace:"nowrap"}}>
            {recoverable ? (
              <div style={{display:"inline-flex",gap:6}}>
                <button
                  onClick={() => handleRecover(j.id, 'check-now')}
                  disabled={isRecovering || noTaskId}
                  title={noTaskId ? "No upstream task ID, can't check" : "One-shot upstream check + finalize if ready (synchronous, slow)"}
                  style={{padding:"4px 8px",fontSize:10,fontWeight:600,borderRadius:6,background:"rgba(91,191,184,0.1)",border:"1px solid rgba(91,191,184,0.35)",color:"#5BBFB8",cursor:isRecovering?"wait":"pointer",opacity:(isRecovering||noTaskId)?0.4:1}}>
                  {isRecovering ? "…" : "Check Now"}
                </button>
                {isInFlight && (
                  <button
                    onClick={() => handleRecover(j.id, 'resume')}
                    disabled={isRecovering || noTaskId}
                    title={noTaskId ? "No upstream task ID, can't resume" : "Resume polling in the background (up to 24 minutes)"}
                    style={{padding:"4px 8px",fontSize:10,fontWeight:600,borderRadius:6,background:"rgba(238,255,68,0.08)",border:`1px solid ${Y}33`,color:Y,cursor:isRecovering?"wait":"pointer",opacity:(isRecovering||noTaskId)?0.4:1}}>
                    Resume
                  </button>
                )}
                {isInFlight && (
                  <button
                    onClick={() => handleRecover(j.id, 'mark-failed')}
                    disabled={isRecovering}
                    title="Mark this job failed (frees up the slot)"
                    style={{padding:"4px 8px",fontSize:10,fontWeight:600,borderRadius:6,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,color:T.dangerText,cursor:isRecovering?"wait":"pointer",opacity:isRecovering?0.4:1}}>
                    Fail
                  </button>
                )}
              </div>
            ) : "—"}
          </td>
        </tr>
      );
    };

    const TableHead = () => (
      <thead>
        <tr style={{borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"left"}}>Status</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"left"}}>Model</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"center"}}>Dur</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"left"}}>User</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"right"}}>Created</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"right"}}>Credits</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"right"}}>Cost</th>
          <th style={{padding:"8px 10px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",color:T.text3,textAlign:"right"}}></th>
        </tr>
      </thead>
    );

    return (
      <div style={{display:"flex",flexDirection:"column",gap:24}}>
        <PageLoader ready={!queueLoading}/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:16,fontWeight:800,color:T.title}}>Job Queue</div>
          <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3,fontSize:10}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:T.success,animation:"pulse 1.5s ease-in-out infinite"}}/>
            Live · refresh 5s
          </div>
        </div>

        {meta.indexBuilding && (
          <div style={{padding:"10px 14px",background:`${Y}10`,border:`1px solid ${Y}30`,borderRadius:10,color:T.title,fontSize:12,lineHeight:1.5}}>
            <strong style={{color:Y}}>Firestore indexes are still building.</strong>{" "}
            The queue and history will populate once they finish (typically a couple of minutes).
            You can monitor progress in the Firebase console.
          </div>
        )}

        {/* Capacity stats */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <Stat label="Studio Kling" value={`${meta.studioKlingActive ?? 0} / ${meta.studioKlingConcurrentMax ?? "—"}`} sub="concurrent in flight" />
          <Stat label="Studio Grok" value={`${meta.studioGrokRpmCurrent ?? 0} / ${meta.studioGrokRpmMax ?? "—"}`} sub="per rolling 60s" />
          <Stat label="Queued" value={`${queuedKling + queuedGrok}`} sub={`${queuedKling} Kling · ${queuedGrok} Grok`} />
          <Stat label="Active" value={`${active.length}`} sub="queued or processing" />
        </div>

        {/* Active jobs */}
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.06)",fontSize:11,fontWeight:700,color:T.text2,letterSpacing:0.5,textTransform:"uppercase"}}>
            Active ({active.length})
          </div>
          <div style={{maxHeight:400,overflowY:"auto",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            {active.length === 0 ? (
              <div style={{padding:"24px",textAlign:"center",color:T.text3,fontSize:12}}>No active jobs</div>
            ) : (
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
                <TableHead/>
                <tbody>
                  {active.map((j, i) => <Row key={j.id} j={j} last={i===active.length-1}/>)}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* History */}
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.06)",fontSize:11,fontWeight:700,color:T.text2,letterSpacing:0.5,textTransform:"uppercase"}}>
            Recent History (last {history.length})
          </div>
          {history.length === 0 ? (
            <div style={{padding:"24px",textAlign:"center",color:T.text3,fontSize:12}}>No history</div>
          ) : (
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
                <TableHead/>
                <tbody>
                  {history.map((j, i) => <Row key={j.id} j={j} last={i===history.length-1}/>)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Main Layout ────────────────────────────────────────────────────────────
  return (
    <div>
      {renderGiftDialog()}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        .a-tab { transition: background 0.15s, color 0.15s, border-color 0.15s !important; }
        .a-tab:hover { background: ${T.surface2} !important; }
        .a-edit { transition: color 0.15s !important; }
        .a-edit:hover { color: ${T.title} !important; }
        .a-del { transition: opacity 0.15s !important; }
        .a-del:hover { opacity: 0.7 !important; }
        .a-cancel { transition: color 0.15s !important; }
        .a-cancel:hover { color: ${T.text} !important; }
      `}</style>
      {/* Tabs render into the page header (see useEffect above). The
          content area takes the full width of the route container. */}
      <div style={{minWidth:0,position:"relative"}}>
        {tab === "users" && renderUsers()}
        {tab === "finances" && renderFinances()}
        {tab === "appconfig" && renderAppConfig()}
        {tab === "jobqueue" && (jobDetailId ? <AdminJobDetailView jobId={jobDetailId}/> : renderJobQueue())}
      </div>
    </div>
  );
}
